import React, { useEffect, useLayoutEffect, useState, useContext, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import axios from "../utils/axiosInstance";
import { UserContext } from "../utils/userContext";
import PageHeader from "../components/ui/PageHeader";
import PageWrapper from "../components/ui/PageWrapper";
import AdminBreadcrumb from "../components/admin/AdminBreadcrumb";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import DateRangePicker from "../components/ui/DateRangePicker";
import PhotoCarouselModal from "../components/PhotoCarouselModal";
import { useBackNavigation } from "../navigation/BackNavigationContext";
import ProjectTasksSection from "../components/ProjectTasks/ProjectTasksSection";
import AllTasksTreeView from "../components/TaskTimeEntry/AllTasksTreeView";
import AddressAutocomplete from "../components/ui/AddressAutocomplete";
import { FaDownload, FaEye, FaEyeSlash, FaEdit, FaTrash, FaPlus, FaFolder, FaFolderPlus, FaHome, FaArrowLeft, FaMapMarkerAlt, FaCalendar, FaMoneyBillWave, FaTasks, FaListUl, FaEllipsisV, FaTimes } from "react-icons/fa";
import ProjectStats from "../components/ProjectStats";
import { recordProjectView } from "../utils/projectViewTracking";
import TaskTimeEntryList from "../components/TaskTimeEntry/TaskTimeEntryList";
import TaskPickerModal from "../components/TaskTimeEntry/TaskPickerModal";
import { getRecentTasks, addRecentTask } from "../utils/recentTasksStorage";
import { getDisplayTextFromTranslations, displayText } from "../utils/translationDisplay";
import { useT } from "../contexts/TranslationContext";
import ProjectStatusSymbol from "../components/project/ProjectStatusSymbol";
import ExpectedMaterialCostFormModal from "../components/ProjectEconomy/ExpectedMaterialCostFormModal";
import CustomerEconomyHelp from "../components/ProjectEconomy/CustomerEconomyHelp";
import { formatExpectedCostKr } from "../components/ProjectEconomy/expectedCostUtils";
import {
  perTaskMarginForRow,
  perTaskMargins,
  perTaskPriceParts,
  sumDisplayedPerTaskTotals,
} from "../components/ProjectEconomy/perTaskEconomy";
import ProjectClientSharePanel from "../components/ProjectClientSharePanel";
import TimeReportMarkerFilter, { timeReportMarkerLabels } from "../components/TimeReportMarkerFilter";

/** Ref-counted `document.body` overflow lock so stacked modals restore scroll only when the last closes. */
let modalScrollLockDepth = 0;
let modalScrollLockPreviousOverflow = "";

function pushModalScrollLock() {
  if (modalScrollLockDepth === 0) {
    modalScrollLockPreviousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  modalScrollLockDepth += 1;
}

function popModalScrollLock() {
  modalScrollLockDepth = Math.max(0, modalScrollLockDepth - 1);
  if (modalScrollLockDepth === 0) {
    document.body.style.overflow = modalScrollLockPreviousOverflow;
  }
}

function useModalScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    pushModalScrollLock();
    return () => popModalScrollLock();
  }, [active]);
}

function useModalEscapeKey(active, onClose) {
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, onClose]);
}

/** Language name -> flag emoji for description language indicators */
const LANGUAGE_FLAG = {
  English: "🇬🇧",
  Swedish: "🇸🇪",
  Georgian: "🇬🇪",
  Latvian: "🇱🇻",
  Russian: "🇷🇺",
  Ukrainian: "🇺🇦",
};
const getLanguageFlag = (languageName) =>
  languageName && LANGUAGE_FLAG[languageName.trim()] ? LANGUAGE_FLAG[languageName.trim()] : "🌐";

/** Strip trailing [LANGUAGE] tag added by getDisplayTextFromTranslations fallback */
const stripLanguageTag = (text) =>
  (text || "").replace(/\s*\[[A-Z\s]+\]\s*$/, "").trim();

/**
 * ProjectDetailsView
 * - View project metadata and files
 * - Admins: create / edit project, add/delete files
 *
 * Routes wired in App.jsx:
 * - /projects/new     -> creation mode
 * - /projects/:id     -> view
 * - /projects/:id/edit -> edit mode
 *
 * UI: uses shared PageWrapper, Button and Card components for consistency.
 */

const formatEconomyLineHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const s = n.toLocaleString("sv-SE", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return s;
};

/** Margin (kr) may be negative; unlike formatKr for cost columns, always show a number when finite. */
const formatEconomyMarginKr = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return `${Math.round(Number(v)).toLocaleString("sv-SE")} kr`;
};

const sumEconomySubtaskMetrics = (task, fields) => {
  const sums = Object.fromEntries(fields.map((f) => [f, 0]));
  for (const sub of task.subtasks || []) {
    for (const f of fields) {
      sums[f] += Number(sub[f] ?? 0);
    }
  }
  return sums;
};

/** Task-level vs task+subtasks totals for economy task rows (API provides atTaskLevel/includingSubtasks). */
const getEconomyTaskRollups = (task) => {
  const fields = [
    "reportedHours",
    "labourCost",
    "billedAmount",
    "materialsAndSubs",
    "materialsFromInvoices",
    "materialsFromAccruals",
  ];
  const subSum = sumEconomySubtaskMetrics(task, fields);
  const atTaskLevel = task.atTaskLevel ?? Object.fromEntries(fields.map((f) => [f, Number(task[f] ?? 0)]));
  const includingSubtasks =
    task.includingSubtasks ??
    Object.fromEntries(fields.map((f) => [f, Number(atTaskLevel[f] ?? 0) + Number(subSum[f] ?? 0)]));
  return {
    atTaskLevel,
    includingSubtasks,
    hasSubtasks: (task.subtasks || []).length > 0,
  };
};

const customerEconomyAddVat = (v) => Number(v ?? 0) * 1.25;
const customerEconomyScaleMaterials = (v) => Math.ceil(Number(v ?? 0) * 1.15 * 1.25);

/** Kronor already rounded the way a customer-economy cell shows them. */
const formatCustomerEconomyDisplayedKr = (v) => {
  const n = Number(v) || 0;
  return n > 0 ? `${n.toLocaleString("sv-SE")} kr` : "0 kr";
};

/** Kronor and hours as a single customer-economy row shows them, before any further summing. */
const displayedCustomerEconomyRow = (row) => {
  const costInclVatRaw = customerEconomyAddVat(row?.billedAmount);
  const materialsScaled = customerEconomyScaleMaterials(row?.materialsFromInvoices ?? row?.materialsAndSubs ?? 0);
  const expectedScaled = customerEconomyScaleMaterials(row?.materialsFromAccruals ?? 0);
  return {
    reportedHours: Number(row?.reportedHours ?? 0),
    costInclVat: costInclVatRaw > 0 ? Math.round(costInclVatRaw) : 0,
    materialsInclVat: materialsScaled > 0 ? materialsScaled : 0,
    expectedInclVat: expectedScaled > 0 ? expectedScaled : 0,
  };
};

const addDisplayedCustomerEconomy = (a, b) => ({
  reportedHours: a.reportedHours + b.reportedHours,
  costInclVat: a.costInclVat + b.costInclVat,
  materialsInclVat: a.materialsInclVat + b.materialsInclVat,
  expectedInclVat: a.expectedInclVat + b.expectedInclVat,
});

/**
 * Shown task-only figures, and the task total.
 * With subtasks, the total is the task cell plus each subtask cell after each row is rounded.
 */
const getCustomerEconomyDisplayedParts = (task) => {
  const rollups = getEconomyTaskRollups(task);
  const own = displayedCustomerEconomyRow(rollups.hasSubtasks ? rollups.atTaskLevel : task);
  if (!rollups.hasSubtasks) return { own, total: own };
  const total = (task.subtasks || []).reduce(
    (acc, sub) => addDisplayedCustomerEconomy(acc, displayedCustomerEconomyRow(sub)),
    own
  );
  return { own, total };
};

/** Parenthetical task total when subtasks exist, otherwise the task's own shown cell. */
const getCustomerEconomyDisplayedTaskTotals = (task) => getCustomerEconomyDisplayedParts(task).total;

const sumCustomerEconomyDisplayedTaskTotals = (tasks) =>
  (tasks || []).reduce(
    (acc, task) => {
      const row = getCustomerEconomyDisplayedTaskTotals(task);
      return {
        reportedHours: acc.reportedHours + row.reportedHours,
        costInclVat: acc.costInclVat + row.costInclVat,
        materialsInclVat: acc.materialsInclVat + row.materialsInclVat,
        expectedInclVat: acc.expectedInclVat + row.expectedInclVat,
      };
    },
    { reportedHours: 0, costInclVat: 0, materialsInclVat: 0, expectedInclVat: 0 }
  );

const EconomyTaskMetricStack = ({ primary, total, hasSubtasks, formatTotal }) => {
  if (!hasSubtasks) return primary;
  const totalNode = formatTotal ? formatTotal(total) : total;
  return (
    <div className="flex flex-col items-end leading-tight">
      {primary}
      <span className="italic text-gray-600 tabular-nums">{totalNode}</span>
    </div>
  );
};

/** Group API lines by report, preserving first-seen order of reports (matches API sort). */
function groupEconomyHoursLinesByReport(lines) {
  const groups = [];
  const indexByReportId = new Map();
  for (const line of lines) {
    const rid = String(line.reportId);
    let idx = indexByReportId.get(rid);
    if (idx === undefined) {
      idx = groups.length;
      indexByReportId.set(rid, idx);
      groups.push({
        reportId: rid,
        date: line.date,
        workerName: line.workerName || "",
        segments: [],
        descriptionParts: [],
      });
    }
    const g = groups[idx];
    const path =
      line.subtaskTitle != null && String(line.subtaskTitle).trim() !== ""
        ? `${line.taskTitle} → ${line.subtaskTitle}`
        : line.taskTitle;
    g.segments.push({ path, hours: line.lineHours });
    const d = line.descriptionDisplay?.trim();
    if (d) g.descriptionParts.push(d);
  }
  return groups.map((g) => {
    const seen = new Set();
    const uniqueDesc = [];
    for (const p of g.descriptionParts) {
      if (seen.has(p)) continue;
      seen.add(p);
      uniqueDesc.push(p);
    }
    return {
      ...g,
      totalHours: g.segments.reduce((sum, s) => sum + (Number(s.hours) || 0), 0),
      descriptionCombined: uniqueDesc.join("\n\n"),
    };
  });
}

/** Smaller than text-sm cap height; stays on one row with the label via flex-nowrap segment. */
const HOURS_BADGE_CLASS =
  "inline-block rounded-full border border-black bg-gray-200 px-1 text-[10px] font-medium tabular-nums leading-none h-3 max-h-3 min-h-0 min-w-0 box-border align-middle";

/** Description block for economy hours modal: table-header-style label + clamped body (renders inside a colspan cell). */
const EconomyReportDescriptionSection = ({ text, t }) => {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = useRef(null);
  const trimmed = text?.trim() ?? "";
  const isEmpty = !trimmed;

  useLayoutEffect(() => {
    if (isEmpty) {
      setHasOverflow(false);
      return;
    }
    if (expanded) return;
    const measure = () => {
      const el = textRef.current;
      if (!el) return;
      setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const raf = requestAnimationFrame(() => measure());
    return () => cancelAnimationFrame(raf);
  }, [trimmed, expanded, isEmpty]);

  return (
    <div className="min-w-0">
      <div className="px-2 pt-2 pb-1 font-semibold text-xs uppercase tracking-wide text-gray-600">
        {t("projectDetails.description")}
      </div>
      <div className="px-2 pb-2">
        {isEmpty ? (
          <p className="text-sm text-gray-400">—</p>
        ) : (
          <>
            <div
              ref={textRef}
              className={`text-gray-700 break-words text-sm ${
                expanded ? "whitespace-pre-wrap" : "line-clamp-2 overflow-hidden"
              }`}
            >
              {trimmed}
            </div>
            {hasOverflow && (
              <button
                type="button"
                className="mt-1.5 text-sm text-gray-800 underline decoration-gray-800 underline-offset-2 hover:opacity-80"
                onClick={() => setExpanded((e) => !e)}
                aria-expanded={expanded}
              >
                {expanded ? t("projectDetails.descriptionShowLess") : t("projectDetails.descriptionShowMore")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/** Group invoice rows by calendar date (desc), preserving order within each date. */
function groupEconomyMaterialsInvoicesByDate(invoices) {
  const byDate = new Map();
  for (const inv of invoices) {
    const key = inv.date ? dayjs(inv.date).format("YYYY-MM-DD") : "";
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key).push(inv);
  }
  return [...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

const formatEconomyMaterialsModalAmount = (netSum, customerView) => {
  const n = Number(netSum);
  if (!Number.isFinite(n) || n === 0) return "—";
  const scaled = customerView ? Math.ceil(n * 1.15 * 1.25) : Math.round(n * 1.15);
  return `${scaled.toLocaleString("sv-SE")} kr`;
};

const formatEconomyMaterialsSupplierLabel = (inv, unknownLabel) => {
  const name = inv.supplierName?.trim() || unknownLabel;
  const invoiceNumber = inv.invoiceNumber?.trim();
  return invoiceNumber ? `${name} (${invoiceNumber})` : name;
};

const CUSTOMER_TASK_CSV_DELIMITER = ";";

const economyRangeQueryParams = (range) => {
  const params = {};
  if (range?.from) params.startDate = dayjs(range.from).format("YYYY-MM-DD");
  if (range?.to) params.endDate = dayjs(range.to).format("YYYY-MM-DD");
  return params;
};

const escapeCsvValue = (value, delimiter = CUSTOMER_TASK_CSV_DELIMITER) => {
  if (value == null) return "";
  const s = String(value);
  const needsQuotes = s.includes('"') || s.includes("\n") || s.includes(delimiter);
  if (needsQuotes) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsvFile = (filename, csvString) => {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};

const formatCustomerEconomyAmountInclVat = (netSum) => {
  const n = Number(netSum);
  if (!Number.isFinite(n) || n === 0) return "—";
  return `${Math.ceil(n * 1.15 * 1.25).toLocaleString("sv-SE")} kr`;
};

/** Combined CSV for customer economy task row (time reports, materials, optional expected costs). */
function buildCustomerTaskEconomyDetailCsv({
  heading,
  estimatedCostExVat,
  actualTotalCostInclVat,
  hoursLines,
  invoices,
  accruals,
  t,
}) {
  const addVat = (v) => Number(v ?? 0) * 1.25;
  const formatKrInclVat = (v) => `${Math.round(Number(v)).toLocaleString("sv-SE")} kr`;
  const estimatedInclVat =
    estimatedCostExVat != null && Number(estimatedCostExVat) > 0 ? addVat(estimatedCostExVat) : null;
  const estimatedStr = estimatedInclVat != null ? formatKrInclVat(estimatedInclVat) : "—";
  const actualStr = formatKrInclVat(actualTotalCostInclVat);

  const rows = [
    [t("projectDetails.taskSubtask"), heading],
    [t("projectDetails.estimatedCostInclVat"), `${estimatedStr} / ${actualStr}`],
    [],
    [t("projectDetails.hoursTimeReportsTitle")],
    [
      t("projectDetails.hoursTimeReportsDate"),
      t("projectDetails.hours"),
      t("projectDetails.worker"),
      t("projectDetails.taskSubtask"),
      t("projectDetails.description"),
    ],
  ];

  const groupedHours = groupEconomyHoursLinesByReport(hoursLines || []);
  if (groupedHours.length === 0) {
    rows.push([t("projectDetails.hoursTimeReportsEmpty")]);
  } else {
    for (const row of groupedHours) {
      const taskPath = row.segments
        .map((seg) => `${seg.path} (${formatEconomyLineHours(seg.hours)})`)
        .join("; ");
      rows.push([
        row.date ? dayjs(row.date).format("YYYY-MM-DD") : "—",
        formatEconomyLineHours(row.totalHours),
        row.workerName?.trim() ? row.workerName : t("projectDetails.unknown"),
        taskPath,
        row.descriptionCombined || "",
      ]);
    }
  }

  rows.push(
    [],
    [t("projectDetails.materialsInvoicesTitleCustomer")],
    [
      t("projectDetails.hoursTimeReportsDate"),
      t("projectDetails.materialsInvoicesSupplier"),
      t("projectDetails.materialsInvoicesAmountInclVat"),
    ]
  );

  const dateGroups = groupEconomyMaterialsInvoicesByDate(invoices || []);
  if (dateGroups.length === 0) {
    rows.push([t("projectDetails.materialsInvoicesEmpty")]);
  } else {
    for (const [dateKey, invRows] of dateGroups) {
      for (const inv of invRows) {
        rows.push([
          dateKey ? dayjs(dateKey).format("YYYY-MM-DD") : "—",
          formatEconomyMaterialsSupplierLabel(inv, t("projectDetails.unknown")),
          formatCustomerEconomyAmountInclVat(inv.netSum),
        ]);
      }
    }
  }

  if ((accruals || []).length > 0) {
    rows.push(
      [],
      [t("projectDetails.expectedCostsDetailSubtitleCustomer")],
      [
        t("projectDetails.expectedCostsDescription"),
        t("projectDetails.expectedCostsSupplier"),
        t("projectDetails.expectedCostsAmountInclVat"),
      ]
    );
    for (const accrual of accruals) {
      rows.push([
        accrual.description || "",
        accrual.supplierName?.trim() ? accrual.supplierName : t("projectDetails.unknown"),
        formatCustomerEconomyAmountInclVat(accrual.amount),
      ]);
    }
  }

  return rows.map((row) => row.map(escapeCsvValue).join(CUSTOMER_TASK_CSV_DELIMITER)).join("\n");
}

/** Opens supplier invoice PDF in a new tab (same as admin invoice inbox). */
async function viewEconomyInvoicePdf(invoiceId, errorMessage) {
  try {
    const res = await axios.get(`/admin/invoices/${invoiceId}/pdf`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    console.error("View PDF", err);
    alert(err.response?.data?.message || errorMessage || "Could not load PDF");
  }
}

const EconomyMaterialsDetailModal = ({ state, onClose, t }) => {
  const isOpen = !!state?.open;
  useModalScrollLock(isOpen);
  useModalEscapeKey(isOpen, onClose);

  if (!state?.open) return null;

  const dateGroups = groupEconomyMaterialsInvoicesByDate(state.invoices || []);
  const pdfErrorMessage = t("projectDetails.materialsInvoicesPdfError");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="economy-materials-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex justify-between items-start border-b shrink-0 gap-4">
          <div className="min-w-0">
            <h3 id="economy-materials-modal-title" className="text-lg font-semibold text-gray-900 truncate">
              {state.heading}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {state.customerView
                ? t("projectDetails.materialsInvoicesTitleCustomer")
                : t("projectDetails.materialsInvoicesTitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            aria-label={t("projectDetails.previewClose")}
          >
            <FaTimes className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {state.loading && (
            <p className="text-sm text-gray-500 text-center py-8">{t("projectDetails.driveLoading")}</p>
          )}
          {state.error && !state.loading && (
            <p className="text-sm text-red-600 text-center py-6">{state.error}</p>
          )}
          {!state.loading && !state.error && dateGroups.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">{t("projectDetails.materialsInvoicesEmpty")}</p>
          )}
          {!state.loading && !state.error && dateGroups.length > 0 && (
            <div className="overflow-x-auto rounded border border-gray-100">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="p-2 font-semibold whitespace-nowrap">{t("projectDetails.materialsInvoicesSupplier")}</th>
                    <th className="p-2 font-semibold text-right whitespace-nowrap">
                      {state.customerView
                        ? t("projectDetails.materialsInvoicesAmountInclVat")
                        : t("projectDetails.materialsInvoicesAmount")}
                    </th>
                    <th className="p-2 font-semibold text-right whitespace-nowrap w-24">
                      {t("projectDetails.materialsInvoicesPdf")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dateGroups.map(([dateKey, rows]) => (
                    <React.Fragment key={dateKey || "no-date"}>
                      <tr className="bg-gray-50/80 border-b border-gray-100">
                        <td colSpan={3} className="p-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                          {dateKey ? dayjs(dateKey).format("YYYY-MM-DD") : "—"}
                        </td>
                      </tr>
                      {rows.map((inv) => (
                        <tr key={String(inv.invoiceId)} className="border-b border-gray-50 align-top">
                          <td className="p-2 text-gray-800">
                            {formatEconomyMaterialsSupplierLabel(inv, t("projectDetails.unknown"))}
                          </td>
                          <td className="p-2 text-right tabular-nums text-gray-900 font-medium whitespace-nowrap">
                            {formatEconomyMaterialsModalAmount(inv.netSum, state.customerView)}
                          </td>
                          <td className="p-2 text-right whitespace-nowrap">
                            {inv.gmailIngestMessageId ? (
                              <button
                                type="button"
                                className="text-gray-900 underline decoration-gray-900 underline-offset-2 hover:opacity-80 text-sm"
                                onClick={() => {
                                  void viewEconomyInvoicePdf(inv.invoiceId, pdfErrorMessage);
                                }}
                              >
                                {t("projectDetails.materialsInvoicesViewPdf")}
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EconomyExpectedCostsDetailModal = ({
  state,
  onClose,
  onSettle,
  onEdit,
  onDelete,
  actionId,
  t,
}) => {
  const isOpen = !!state?.open;
  const customerView = !!state?.customerView;
  useModalScrollLock(isOpen);
  useModalEscapeKey(isOpen, onClose);

  if (!state?.open) return null;

  const formatAmount = (amount) => {
    const n = Number(amount ?? 0);
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (customerView) {
      const scaled = Math.ceil(n * 1.15 * 1.25);
      return `${scaled.toLocaleString("sv-SE")} kr`;
    }
    return formatExpectedCostKr(n);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="economy-expected-costs-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex justify-between items-start border-b shrink-0 gap-4">
          <div className="min-w-0">
            <h3 id="economy-expected-costs-modal-title" className="text-lg font-semibold text-gray-900 truncate">
              {state.heading}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {customerView
                ? t("projectDetails.expectedCostsDetailSubtitleCustomer")
                : t("projectDetails.expectedCostsDetailSubtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            aria-label={t("projectDetails.previewClose")}
          >
            <FaTimes className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {state.loading && (
            <p className="text-sm text-gray-500 text-center py-8">{t("projectDetails.driveLoading")}</p>
          )}
          {state.error && !state.loading && (
            <p className="text-sm text-red-600 text-center py-6">{state.error}</p>
          )}
          {!state.loading && !state.error && (state.accruals || []).length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">{t("projectDetails.expectedCostsDetailEmpty")}</p>
          )}
          {!state.loading && !state.error && (state.accruals || []).length > 0 && (
            <div className="overflow-x-auto rounded border border-gray-100">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="p-2 font-semibold">{t("projectDetails.expectedCostsDescription")}</th>
                    <th className="p-2 font-semibold">{t("projectDetails.expectedCostsSupplier")}</th>
                    <th className="p-2 font-semibold text-right whitespace-nowrap">
                      {customerView
                        ? t("projectDetails.expectedCostsAmountInclVat")
                        : t("projectDetails.expectedCostsAmountExVat")}
                    </th>
                    <th className="p-2 font-semibold text-right whitespace-nowrap">{t("projectDetails.expectedCostsActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(state.accruals || []).map((a) => {
                    const accrualId = a.accrualId || a._id;
                    return (
                      <tr key={String(accrualId)} className="border-b border-gray-50 align-top">
                        <td className="p-2 text-gray-800">
                          <div>{a.description}</div>
                          {a.notes ? <div className="text-xs text-gray-500 mt-0.5">{a.notes}</div> : null}
                        </td>
                        <td className="p-2 text-gray-600">
                          {a.supplierName?.trim() ? a.supplierName : t("projectDetails.unknown")}
                        </td>
                        <td className="p-2 text-right tabular-nums text-gray-900 font-medium whitespace-nowrap">
                          {formatAmount(a.amount)}
                        </td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <button
                            type="button"
                            className="text-sm text-brand hover:underline mr-2 disabled:opacity-50"
                            disabled={actionId === accrualId}
                            onClick={() => onSettle?.(accrualId)}
                          >
                            {t("projectDetails.expectedCostsSettle")}
                          </button>
                          <button
                            type="button"
                            className="text-sm text-gray-700 hover:underline mr-2"
                            onClick={() => onEdit?.(a)}
                          >
                            {t("projectDetails.expectedCostsEdit")}
                          </button>
                          <button
                            type="button"
                            className="text-sm text-red-600 hover:underline disabled:opacity-50"
                            disabled={actionId === accrualId}
                            onClick={() => onDelete?.(accrualId)}
                          >
                            {t("projectDetails.delete")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EconomyHoursDetailModal = ({ state, onClose, t }) => {
  const isOpen = !!state?.open;
  useModalScrollLock(isOpen);
  useModalEscapeKey(isOpen, onClose);

  if (!state?.open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="economy-hours-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex justify-between items-start border-b shrink-0 gap-4">
          <div className="min-w-0">
            <h3 id="economy-hours-modal-title" className="text-lg font-semibold text-gray-900 truncate">
              {state.heading}
            </h3>
            <p className="text-xs text-gray-500 mt-1">{t("projectDetails.hoursTimeReportsTitle")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            aria-label={t("projectDetails.previewClose")}
          >
            <FaTimes className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {state.loading && (
            <p className="text-sm text-gray-500 text-center py-8">{t("projectDetails.driveLoading")}</p>
          )}
          {state.error && !state.loading && (
            <p className="text-sm text-red-600 text-center py-6">{state.error}</p>
          )}
          {!state.loading && !state.error && state.lines.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">{t("projectDetails.hoursTimeReportsEmpty")}</p>
          )}
          {!state.loading && !state.error && state.lines.length > 0 && (
            <div className="overflow-x-auto rounded border border-gray-100">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="p-2 font-semibold whitespace-nowrap">{t("projectDetails.hoursTimeReportsDate")}</th>
                    <th className="p-2 font-semibold text-right whitespace-nowrap">{t("projectDetails.hours")}</th>
                    <th className="p-2 font-semibold whitespace-nowrap">{t("projectDetails.worker")}</th>
                    <th className="p-2 font-semibold min-w-[12rem]">{t("projectDetails.taskSubtask")}</th>
                  </tr>
                </thead>
                <tbody>
                  {groupEconomyHoursLinesByReport(state.lines).map((row) => (
                    <React.Fragment key={row.reportId}>
                      <tr className="align-top border-b-0">
                        <td className="p-2 whitespace-nowrap text-gray-800">
                          {row.date ? dayjs(row.date).format("YYYY-MM-DD") : "—"}
                        </td>
                        <td className="p-2 text-right tabular-nums text-gray-900 font-medium">
                          {formatEconomyLineHours(row.totalHours)}
                        </td>
                        <td className="p-2 text-gray-800 whitespace-nowrap">
                          {row.workerName?.trim() ? row.workerName : t("projectDetails.unknown")}
                        </td>
                        <td className="p-2 text-gray-800 text-sm leading-normal">
                          {/*
                          Each segment is flex-none w-fit so it never grows to fill a flex line.
                          Otherwise a segment on a row with extra space behaved like a full-width bar,
                          the label grew, and the hour badge sat at the far right (looked “every other”).
                        */}
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            {row.segments.map((seg, i) => (
                              <span
                                key={`${row.reportId}-seg-${i}`}
                                className="inline-block w-fit max-w-full min-w-0 flex-none"
                              >
                                <span className="min-w-0 break-words">{seg.path}</span>
                                <span className={`${HOURS_BADGE_CLASS} ml-1`}>{formatEconomyLineHours(seg.hours)}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 border-t border-gray-100 bg-gray-50/60">
                        <td colSpan={4} className="p-0 align-top">
                          <EconomyReportDescriptionSection text={row.descriptionCombined} t={t} />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PreviewModal = ({ file, onClose, t }) => {
  const isOpen = !!file;
  useModalScrollLock(isOpen);
  useModalEscapeKey(isOpen, onClose);

  if (!file) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg max-w-3xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex justify-between items-center border-b">
          <div id="preview-modal-title" className="text-lg font-bold">
            {file.originalName || file.filename}
          </div>
          <button onClick={onClose} className="text-gray-700 px-2">
            {t("projectDetails.previewClose")}
          </button>
        </div>
        <div className="p-4">
          {file.mimeType && file.mimeType.startsWith("image/") ? (
            <img
              src={file.url || file.filename}
              alt={file.originalName}
              className="w-full h-auto"
            />
          ) : file.mimeType === "application/pdf" ? (
            <iframe
              src={file.url || file.filename}
              title={file.originalName}
              className="w-full h-[70vh]"
            />
          ) : (
            <div className="p-4">
              {t("projectDetails.previewNotAvailable")}{" "}
              <a
                href={file.url || file.filename}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600"
              >
                {t("projectDetails.openFile")}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExportProjectCsvModal = ({
  isOpen,
  startDate,
  onStartDateChange,
  onClose,
  onConfirm,
  isSubmitting,
  filterSummary = "",
  t,
}) => {
  useModalScrollLock(isOpen);
  useModalEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={t("timeReports.exportToCsv")}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">{t("timeReports.exportToCsv")}</h3>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            aria-label={t("projectDetails.cancel")}
            disabled={isSubmitting}
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t("projectDetails.startDate")}
        </label>
        <input
          type="date"
          className="w-full rounded border px-3 py-2 text-sm"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          disabled={isSubmitting}
        />
        {filterSummary ? (
          <p className="mt-3 text-sm text-gray-600">
            {t("projectDetails.timeReportFilterExportNote", { markers: filterSummary })}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
            disabled={isSubmitting}
          >
            {t("projectDetails.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!startDate || isSubmitting}
          >
            {isSubmitting ? "Exporting..." : t("timeReports.exportToCsv")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProjectDetailsView = () => {
  const { id } = useParams(); // may be undefined for /projects/new route handling (App maps /projects/new to same component)
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const { t } = useT();
  const { setPageFallback, markAsRoot, excludeFromHistory } =
    useBackNavigation();

  const isNew = location.pathname === "/projects/new";
  const isEditPath = location.pathname.endsWith("/edit");
  const canEdit = user && user.isAdmin;

  useEffect(() => {
    // Back navigation strategy:
    // - New project mode: mark as root (no back button shown, prevents navigation away)
    // - Edit mode: set fallback to view mode (allows canceling edits) + exclude from history to prevent loops
    // - View mode: no fallback (rely on automatic navigation stack for proper nested navigation)
    if (isNew) {
      setPageFallback("/projects");
      markAsRoot(true);
      return;
    }

    if (isEditPath && id) {
      // Edit mode: back should go to view mode for this project
      setPageFallback(`/projects/${id}`);
      markAsRoot(false);
      // Exclude edit page from history to prevent navigation loops
      // (edit uses fallback, so shouldn't be added to the stack)
      excludeFromHistory(true);
    } else {
      // View mode: no fallback - let the navigation stack handle it naturally
      // This allows proper nested navigation (e.g., project -> task -> subtask)
      setPageFallback(null);
      markAsRoot(false);
      excludeFromHistory(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, isEditPath, id]);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  // Google Drive client state
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [drivePageToken, setDrivePageToken] = useState(null);
  const [driveUploading, setDriveUploading] = useState(false);
  
  // Google Drive folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = project root
  const [folderStack, setFolderStack] = useState([]); // [{id, name}, ...]
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [loadingDriveFileId, setLoadingDriveFileId] = useState(null); // Track which file is loading

  // Photos state (project photos aggregated from time reports)
  const [photos, setPhotos] = useState([]); // items: { url, date, description, reportId }
  const [photosPage, setPhotosPage] = useState(1);
  const [photosHasMore, setPhotosHasMore] = useState(false);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    items: [],
    index: 0,
  });

  // Time reports state (paginated)
  const [timeReports, setTimeReports] = useState([]);
  const [timeReportsLoading, setTimeReportsLoading] = useState(false);
  const [timeReportsHasMore, setTimeReportsHasMore] = useState(false);
  const [timeReportsSkip, setTimeReportsSkip] = useState(0);
  const [showAllTimeReports, setShowAllTimeReports] = useState(false);
  const [timeReportMarkers, setTimeReportMarkers] = useState([]);
  const [exportError, setExportError] = useState("");
  const [economyExportError, setEconomyExportError] = useState("");
  const [economyExportModalOpen, setEconomyExportModalOpen] = useState(false);
  const [economyExportStartDate, setEconomyExportStartDate] = useState(() =>
    dayjs().subtract(3, "month").format("YYYY-MM-DD")
  );
  const [economyExportSubmitting, setEconomyExportSubmitting] = useState(false);
  const [projectExportModalOpen, setProjectExportModalOpen] = useState(false);
  const [projectExportStartDate, setProjectExportStartDate] = useState(() =>
    dayjs().subtract(3, "month").format("YYYY-MM-DD")
  );
  const [projectExportSubmitting, setProjectExportSubmitting] = useState(false);
  const [timeReportsMenuOpen, setTimeReportsMenuOpen] = useState(false);
  const [previousTimeReportsCount, setPreviousTimeReportsCount] = useState(0);
  const timeReportsContainerRef = useRef(null);
  const timeReportsRequestRef = useRef(0);
  const economySectionRef = useRef(null);

  // Inline edit state for time reports
  const [editingReportId, setEditingReportId] = useState(null);
  const [editForm, setEditForm] = useState({ projectId: "", date: "", description: "", hours: "", taskEntries: [] });
  const [editTaskBundle, setEditTaskBundle] = useState(null);
  const [editProjectsList, setEditProjectsList] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [isInlineTaskPickerOpen, setIsInlineTaskPickerOpen] = useState(false);
  const [editRecentTasks, setEditRecentTasks] = useState([]);
  const [clientVisibilitySavingId, setClientVisibilitySavingId] = useState(null);

  // Economy state (admin only)
  const [economy, setEconomy] = useState(null);
  const [economyLoading, setEconomyLoading] = useState(false);
  const [economyDateRange, setEconomyDateRange] = useState(() => ({
    from: dayjs().subtract(29, "day").startOf("day").toDate(),
    to: dayjs().endOf("day").toDate(),
  }));
  const [economyView, setEconomyView] = useState("per_worker");
  const [economyByTask, setEconomyByTask] = useState(null);
  const [economyByTaskLoading, setEconomyByTaskLoading] = useState(false);
  const [economyByCustomer, setEconomyByCustomer] = useState(null);
  const [economyByCustomerLoading, setEconomyByCustomerLoading] = useState(false);
  const [economyHoursModal, setEconomyHoursModal] = useState({
    open: false,
    heading: "",
    loading: false,
    error: null,
    lines: [],
  });
  const [economyMaterialsModal, setEconomyMaterialsModal] = useState({
    open: false,
    heading: "",
    loading: false,
    error: null,
    invoices: [],
    customerView: false,
  });
  const [economyExpectedCostsModal, setEconomyExpectedCostsModal] = useState({
    open: false,
    heading: "",
    loading: false,
    error: null,
    accruals: [],
    customerView: false,
  });
  const [expectedCostFormModalOpen, setExpectedCostFormModalOpen] = useState(false);
  const [editingExpectedCost, setEditingExpectedCost] = useState(null);
  const [expectedCostActionId, setExpectedCostActionId] = useState(null);
  const [customerTaskCsvExportKey, setCustomerTaskCsvExportKey] = useState(null);

  // Project task bundle for view-mode tasks list (All tasks tree)
  const [projectTaskBundle, setProjectTaskBundle] = useState(null);
  const [projectTaskBundleLoading, setProjectTaskBundleLoading] = useState(false);

  // Download-all state / progress
  const [photosDownloading, setPhotosDownloading] = useState(false);
  const [photosDownloadProgress, setPhotosDownloadProgress] = useState({
    loaded: 0,
    total: 0,
    percent: 0,
  });
  const [photosDownloadPart, setPhotosDownloadPart] = useState(0);
  const [photosDownloadTotalParts, setPhotosDownloadTotalParts] = useState(0);
  const photosDownloadControllerRef = useRef(null);
  const photosContainerRef = useRef(null);
  const [previousPhotoCount, setPreviousPhotoCount] = useState(0);

  // form state (used for create/edit)
  const [form, setForm] = useState({
    name: "",
    address: "",
    zipCode: "",
    city: "",
    startDate: "",
    endDate: "",
    status: "",
    description: "",
    manager: "",
    showTasksInTimeReport: true,
  });

  // Worker rates state (for edit mode)
  const [workerRates, setWorkerRates] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState("");
  const [selectedWorkerRate, setSelectedWorkerRate] = useState("");
  const [users, setUsers] = useState([]);

  // Fetch users for worker rates dropdown
  const fetchUsers = async () => {
    try {
      const response = await axios.get("/users");
      setUsers(response.data.items || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Worker rate management functions
  const handleAddWorkerRate = () => {
    if (!selectedWorker || !selectedWorkerRate) {
      alert(t("projectDetails.pleaseSelectWorkerAndRate"));
      return;
    }

    const rate = parseFloat(selectedWorkerRate);
    if (isNaN(rate) || rate <= 0) {
      alert(t("projectDetails.pleaseEnterValidRate"));
      return;
    }

    // Check if worker already has a rate
    if (workerRates.some((wr) => wr.workerId === selectedWorker)) {
      alert("This worker already has a rate. Please remove it first.");
      return;
    }

    setWorkerRates([
      ...workerRates,
      {
        workerId: selectedWorker,
        hourlyRate: rate,
      },
    ]);
    setSelectedWorker("");
    setSelectedWorkerRate("");
  };

  const handleRemoveWorkerRate = (workerId) => {
    setWorkerRates(workerRates.filter((wr) => wr.workerId !== workerId));
  };

  const getWorkerName = (workerId) => {
    const foundUser = users.find((u) => u._id === workerId);
    return foundUser ? `${foundUser.firstname} ${foundUser.lastname}` : t("projectDetails.unknown");
  };

  // Fetch photos for this project (paginated)
  async function fetchPhotos(page = 1) {
    if (!id) return;
    setPhotosLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/projects/${id}/photos`, {
        params: { page, limit: 24 },
      });
      const { items, hasMore } = res.data || {};
      if (page === 1) {
        setPhotos(items || []);
      } else {
        setPhotos((p) => [...p, ...(items || [])]);
      }
      setPhotosPage(page);
      setPhotosHasMore(!!hasMore);
    } catch (err) {
      console.error("Failed to fetch project photos", err);
      // don't surface as blocking error; show in UI if desired
    } finally {
      setPhotosLoading(false);
    }
  }

  // Fetch time reports for this project (paginated). useShowAllLimit: when true, request 100 per page (for "Show all" mode).
  // A marker filter loads every match at once (server cap 2000); those lists are not paged.
  async function fetchTimeReports(reset = false, useShowAllLimit = false, markersOverride) {
    if (!id) return;
    const markers = markersOverride ?? timeReportMarkers;
    const filtered = markers.length > 0;
    const requestId = ++timeReportsRequestRef.current;
    setTimeReportsLoading(true);
    try {
      const skip = reset || filtered ? 0 : timeReportsSkip;
      const limit = filtered ? 2000 : useShowAllLimit || showAllTimeReports ? 100 : 5;
      const params = { limit, skip };
      if (filtered) params.has = markers.join(",");
      const res = await axios.get(`/projects/${id}/time-reports`, { params });
      if (requestId !== timeReportsRequestRef.current) return;
      const newReports = res.data.reports || [];

      if (reset || filtered) {
        setTimeReports(newReports);
        setTimeReportsSkip(newReports.length);
      } else {
        setTimeReports((prev) => [...prev, ...newReports]);
        setTimeReportsSkip((prev) => prev + newReports.length);
      }

      setTimeReportsHasMore(filtered ? false : res.data.hasMore || false);
    } catch (err) {
      if (requestId !== timeReportsRequestRef.current) return;
      console.error("Failed to fetch time reports", err);
    } finally {
      if (requestId === timeReportsRequestRef.current) setTimeReportsLoading(false);
    }
  }

  function changeTimeReportMarkers(next) {
    setTimeReportMarkers(next);
    setTimeReports([]);
    setTimeReportsSkip(0);
    setTimeReportsHasMore(false);
    fetchTimeReports(true, showAllTimeReports, next);
  }

  // Start inline editing a time report: load projects list and task bundle if task-based
  async function startEditingReport(report) {
    if (report.absence?.isAbsent) return;
    setEditingReportId(report._id);
    setEditError("");
    const dateStr = report.date ? new Date(report.date).toISOString().slice(0, 10) : "";
    const hasTaskEntries = report.taskEntries && report.taskEntries.length > 0;
    setEditForm({
      projectId: report.projectId || id || "",
      date: dateStr,
      description: report.description || "",
      hours: hasTaskEntries ? "" : String(report.hours ?? ""),
      taskEntries: (report.taskEntries || []).map((e, idx) => ({
        ...e,
        hours: String(e.hours ?? ""),
        description: e.description || "",
        title: e.title || t("projectDetails.taskN", { number: idx + 1 }),
        displayName: e.title || t("projectDetails.taskN", { number: idx + 1 }),
      })),
    });
    setEditTaskBundle(null);
    setEditProjectsList([]);
    setEditRecentTasks(user?.userId ? getRecentTasks(user.userId) : []);

    try {
      const [projectsRes, bundleRes] = await Promise.all([
        axios.get("/projects"),
        axios.get(`/task-bundles/projects/${report.projectId || id}/task-bundle`).catch(() => ({ data: null })),
      ]);
      setEditProjectsList(projectsRes.data?.items || []);

      const bundle = bundleRes.data;
      if (bundle?.tasks?.length) {
        setEditTaskBundle(bundle);
        if (hasTaskEntries) {
          setEditForm((prev) => ({
          ...prev,
          taskEntries: (prev.taskEntries || []).map((entry, idx) => {
            const task = bundle.tasks.find((t) => t._id.toString() === (entry.taskId)?.toString());
            let title = entry.title || t("projectDetails.taskN", { number: idx + 1 });
            let showDescriptionInTimeReport = false;
            let requireDescriptionInTimeReport = false;
            let estimatedHours = null;
            let trackedHours = 0;
            let type = "task";
            let taskTitle;
            if (entry.subtaskId && task) {
              const subtask = task.subtasks?.find((st) => st._id.toString() === entry.subtaskId?.toString());
              if (subtask) {
                title = subtask.title;
                showDescriptionInTimeReport = subtask.showDescriptionInTimeReport || false;
                requireDescriptionInTimeReport = subtask.requireDescriptionInTimeReport || false;
                estimatedHours = subtask.estimatedHours;
                trackedHours = subtask.trackedHours || 0;
                type = "subtask";
                taskTitle = task.title;
              }
            } else if (task) {
              title = task.title;
              showDescriptionInTimeReport = task.showDescriptionInTimeReport || false;
              requireDescriptionInTimeReport = task.requireDescriptionInTimeReport || false;
              estimatedHours = task.estimatedHours;
              trackedHours = task.trackedHours || 0;
            }
            return {
              ...entry,
              title,
              displayName: title,
              showDescriptionInTimeReport,
              requireDescriptionInTimeReport,
              type,
              taskTitle,
              estimatedHours,
              trackedHours,
              billingModel: task?.billingModel || null,
            };
          }),
        }));
        }
      }
    } catch (err) {
      console.error("Failed to load edit data", err);
      setEditError(t("projectDetails.failedToLoadEditData"));
    }
  }

  function cancelEditingReport() {
    setEditingReportId(null);
    setEditForm({ projectId: "", date: "", description: "", hours: "", taskEntries: [] });
    setEditTaskBundle(null);
    setEditError("");
    setIsInlineTaskPickerOpen(false);
  }

  async function toggleReportClientVisibility(report) {
    if (!user?.isAdmin || report.absence?.isAbsent || clientVisibilitySavingId) return;
    const nextHidden = !report.hiddenFromClient;
    setClientVisibilitySavingId(report._id);
    setTimeReports((prev) =>
      prev.map((r) =>
        r._id === report._id ? { ...r, hiddenFromClient: nextHidden } : r
      )
    );
    try {
      await axios.patch(`/reports/${report._id}/client-visibility`, {
        hiddenFromClient: nextHidden,
      });
    } catch (err) {
      setTimeReports((prev) =>
        prev.map((r) =>
          r._id === report._id ? { ...r, hiddenFromClient: !nextHidden } : r
        )
      );
      console.error("Failed to update client visibility", err);
      alert(
        err.response?.data?.message || t("projectDetails.clientVisibilityError")
      );
    } finally {
      setClientVisibilitySavingId(null);
    }
  }

  // When project changes in inline edit, fetch that project's task bundle so Add task uses the right list
  useEffect(() => {
    if (!editingReportId || !editForm.projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `/task-bundles/projects/${editForm.projectId}/task-bundle`
        );
        if (!cancelled && res.data?.tasks?.length) {
          setEditTaskBundle(res.data);
        } else if (!cancelled) {
          setEditTaskBundle(null);
        }
      } catch {
        if (!cancelled) setEditTaskBundle(null);
      }
    })();
    return () => { cancelled = true; };
  }, [editingReportId, editForm.projectId]);

  // Close time reports menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timeReportsMenuOpen && !event.target.closest(".time-reports-menu-container")) {
        setTimeReportsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [timeReportsMenuOpen]);

  function handleAddTaskEntryInline(taskItem) {
    setEditForm((prev) => {
      const next = prev.taskEntries || [];
      const norm = (id) => (id === null || id === undefined ? null : id);
      const exists = next.some(
        (e) =>
          e.taskId === taskItem.taskId &&
          norm(e.subtaskId) === norm(taskItem.subtaskId)
      );
      if (exists) return prev;
      // When adding the first task to a legacy report, pre-fill hours from the legacy report
      const initialHours = next.length === 0 ? String(prev.hours ?? "") : "";
      return {
        ...prev,
        taskEntries: [...next, { ...taskItem, hours: initialHours, description: "" }],
      };
    });
    if (user?.userId) {
      addRecentTask(user.userId, taskItem);
      setEditRecentTasks(getRecentTasks(user.userId));
    }
    setIsInlineTaskPickerOpen(false);
  }

  function handleRemoveTaskEntryInline(idx) {
    setEditForm((prev) => ({
      ...prev,
      taskEntries: (prev.taskEntries || []).filter((_, i) => i !== idx),
    }));
  }

  function handleEditTaskHours(entryIdx, value) {
    setEditForm((prev) => {
      const next = [...(prev.taskEntries || [])];
      next[entryIdx] = { ...next[entryIdx], hours: value };
      return { ...prev, taskEntries: next };
    });
  }

  function handleEditTaskDescription(entryIdx, value) {
    setEditForm((prev) => {
      const next = [...(prev.taskEntries || [])];
      next[entryIdx] = { ...next[entryIdx], description: value };
      return { ...prev, taskEntries: next };
    });
  }

  async function saveEditedReport() {
    const reportId = editingReportId;
    if (!reportId) return;
    const hasTaskEntries = editForm.taskEntries?.length > 0;

    if (hasTaskEntries) {
      const invalid = editForm.taskEntries.find((e) => {
        const h = parseFloat(String(e.hours).replace(",", "."));
        return isNaN(h) || h <= 0;
      });
      if (invalid) {
        setEditError("Please enter valid hours for all task entries.");
        return;
      }
      const missingDesc = editForm.taskEntries.find(
        (e) => e.requireDescriptionInTimeReport && !(e.description || "").trim()
      );
      if (missingDesc) {
        setEditError(t("projectDetails.validationTaskDescriptionRequired", { taskName: missingDesc.title || "task" }));
        return;
      }
    } else {
      const hours = parseFloat(String(editForm.hours).replace(",", "."));
      if (isNaN(hours) || hours <= 0) {
        setEditError(t("projectDetails.pleaseEnterValidHours"));
        return;
      }
      if (!(editForm.description || "").trim()) {
        setEditError("Description is required.");
        return;
      }
    }

    setEditSaving(true);
    setEditError("");
    try {
      const formData = new FormData();
      formData.append("date", editForm.date);
      formData.append("projectId", editForm.projectId);
      formData.append("absence", JSON.stringify({ isAbsent: false }));
      formData.append("description", hasTaskEntries ? (editForm.description || "Task time entries") : editForm.description);

      if (hasTaskEntries) {
        formData.append(
          "taskEntries",
          JSON.stringify(
            editForm.taskEntries.map((e) => ({
              bundleId: e.bundleId,
              taskId: e.taskId,
              ...(e.subtaskId ? { subtaskId: e.subtaskId } : {}),
              hours: parseFloat(String(e.hours).replace(",", ".")),
              description: e.description || "",
              title: e.displayName || e.title,
            }))
          )
        );
      } else {
        formData.append("hours", parseFloat(String(editForm.hours).replace(",", ".")));
      }

      const res = await axios.put(`/reports/${reportId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const updated = res.data.report;
      setTimeReports((prev) =>
        prev.map((r) =>
          r._id === reportId
            ? {
                ...r,
                date: updated.date,
                hours: updated.hours,
                description: updated.description,
                projectId: updated.projectId,
                taskEntries: updated.taskEntries || [],
                absence: updated.absence || { isAbsent: false },
              }
            : r
        )
      );
      cancelEditingReport();
    } catch (err) {
      console.error("Failed to update report", err);
      setEditError(err.response?.data?.message || t("projectDetails.failedToSaveReport"));
    } finally {
      setEditSaving(false);
    }
  }

  // Fetch economy data for this project (admin only). Pass rangeOverride when calling from picker so we use the new range before state updates.
  async function fetchEconomy(rangeOverride) {
    if (!id || !user?.isAdmin) return;
    const range = rangeOverride ?? economyDateRange;
    setEconomyLoading(true);
    try {
      const params = economyRangeQueryParams(range);
      const res = await axios.get(`/projects/${id}/economy`, { params });
      setEconomy(res.data);
    } catch (err) {
      console.error("Failed to fetch economy data", err);
      // Don't set blocking error, just log
    } finally {
      setEconomyLoading(false);
    }
  }

  async function fetchEconomyByTask() {
    if (!id || !user?.isAdmin) return;
    setEconomyByTaskLoading(true);
    try {
      const res = await axios.get(`/projects/${id}/economy-by-task`, {
        params: { includeAllSubtaskData: true },
      });
      setEconomyByTask(res.data);
    } catch (err) {
      console.error("Failed to fetch economy by task", err);
      setEconomyByTask({ tasks: [] });
    } finally {
      setEconomyByTaskLoading(false);
    }
  }

  async function fetchEconomyByCustomer(rangeOverride) {
    if (!id || !user?.isAdmin) return;
    const range = rangeOverride ?? economyDateRange;
    setEconomyByCustomerLoading(true);
    try {
      const res = await axios.get(`/projects/${id}/economy-by-task`, {
        params: {
          includeAllSubtaskData: true,
          excludeUnbillableMaterials: true,
          ...economyRangeQueryParams(range),
        },
      });
      setEconomyByCustomer(res.data);
    } catch (err) {
      console.error("Failed to fetch economy by customer", err);
      setEconomyByCustomer({ tasks: [] });
    } finally {
      setEconomyByCustomerLoading(false);
    }
  }

  async function openEconomyTaskHoursModal({ taskId, subtaskId, rolledSubtaskIds, heading }) {
    if (!id || !taskId || !user?.isAdmin) return;
    setEconomyHoursModal({
      open: true,
      heading: heading || "",
      loading: true,
      error: null,
      lines: [],
    });
    try {
      const params = { taskId: String(taskId) };
      if (subtaskId) params.subtaskId = String(subtaskId);
      else if (rolledSubtaskIds?.length)
        params.rolledSubtaskIds = rolledSubtaskIds.map(String).join(",");
      if (economyView === "customer") Object.assign(params, economyRangeQueryParams(economyDateRange));
      const res = await axios.get(`/projects/${id}/task-hours-detail`, { params });
      setEconomyHoursModal((s) => ({
        ...s,
        loading: false,
        lines: res.data?.lines || [],
      }));
    } catch (err) {
      console.error("Failed to fetch task hours detail", err);
      const msg =
        err?.response?.data?.message || t("projectDetails.hoursTimeReportsError");
      setEconomyHoursModal((s) => ({ ...s, loading: false, error: msg }));
    }
  }

  function closeEconomyHoursModal() {
    setEconomyHoursModal({
      open: false,
      heading: "",
      loading: false,
      error: null,
      lines: [],
    });
  }

  async function openEconomyTaskMaterialsModal({
    taskId,
    subtaskId,
    rolledSubtaskIds,
    heading,
    customerView = false,
  }) {
    if (!id || !taskId || !user?.isAdmin) return;
    setEconomyMaterialsModal({
      open: true,
      heading: heading || "",
      loading: true,
      error: null,
      invoices: [],
      customerView: !!customerView,
    });
    try {
      const params = { taskId: String(taskId) };
      if (subtaskId) params.subtaskId = String(subtaskId);
      else if (rolledSubtaskIds?.length)
        params.rolledSubtaskIds = rolledSubtaskIds.map(String).join(",");
      if (customerView) {
        params.excludeUnbillableMaterials = true;
        Object.assign(params, economyRangeQueryParams(economyDateRange));
      }
      const res = await axios.get(`/projects/${id}/task-materials-detail`, { params });
      setEconomyMaterialsModal((s) => ({
        ...s,
        loading: false,
        invoices: res.data?.invoices || [],
      }));
    } catch (err) {
      console.error("Failed to fetch task materials detail", err);
      const msg =
        err?.response?.data?.message || t("projectDetails.materialsInvoicesError");
      setEconomyMaterialsModal((s) => ({ ...s, loading: false, error: msg }));
    }
  }

  function closeEconomyMaterialsModal() {
    setEconomyMaterialsModal({
      open: false,
      heading: "",
      loading: false,
      error: null,
      invoices: [],
      customerView: false,
    });
  }

  async function openEconomyTaskExpectedCostsModal({
    taskId,
    subtaskId,
    rolledSubtaskIds,
    heading,
    customerView = false,
  }) {
    if (!id || !taskId || !user?.isAdmin) return;
    setEconomyExpectedCostsModal({
      open: true,
      heading: heading || "",
      loading: true,
      error: null,
      accruals: [],
      customerView: !!customerView,
      taskContext: { taskId, subtaskId, rolledSubtaskIds, heading, customerView },
    });
    try {
      const params = { taskId: String(taskId) };
      if (subtaskId) params.subtaskId = String(subtaskId);
      else if (rolledSubtaskIds?.length)
        params.rolledSubtaskIds = rolledSubtaskIds.map(String).join(",");
      const res = await axios.get(`/projects/${id}/task-expected-costs-detail`, { params });
      setEconomyExpectedCostsModal((s) => ({
        ...s,
        loading: false,
        accruals: res.data?.accruals || [],
      }));
    } catch (err) {
      console.error("Failed to fetch expected costs detail", err);
      const msg =
        err?.response?.data?.message || t("projectDetails.expectedCostsDetailError");
      setEconomyExpectedCostsModal((s) => ({ ...s, loading: false, error: msg }));
    }
  }

  async function exportCustomerTaskEconomyCsv({
    taskId,
    subtaskId,
    rolledSubtaskIds,
    heading,
    estimatedCostExVat,
    actualTotalCostInclVat,
  }) {
    if (!id || !taskId || !user?.isAdmin) return;
    const exportKey = `${taskId}${subtaskId ? `-${subtaskId}` : ""}`;
    setCustomerTaskCsvExportKey(exportKey);
    setEconomyExportError("");
    try {
      const params = { taskId: String(taskId) };
      if (subtaskId) params.subtaskId = String(subtaskId);
      else if (rolledSubtaskIds?.length)
        params.rolledSubtaskIds = rolledSubtaskIds.map(String).join(",");
      Object.assign(params, economyRangeQueryParams(economyDateRange));

      const [hoursRes, materialsRes, expectedRes] = await Promise.all([
        axios.get(`/projects/${id}/task-hours-detail`, { params }),
        axios.get(`/projects/${id}/task-materials-detail`, {
          params: { ...params, excludeUnbillableMaterials: true },
        }),
        axios.get(`/projects/${id}/task-expected-costs-detail`, { params }),
      ]);

      const csvString = buildCustomerTaskEconomyDetailCsv({
        heading: heading || "—",
        estimatedCostExVat,
        actualTotalCostInclVat,
        hoursLines: hoursRes.data?.lines || [],
        invoices: materialsRes.data?.invoices || [],
        accruals: expectedRes.data?.accruals || [],
        t,
      });

      const safeProjectName = (project?.name || "project").replace(/\s+/g, "_");
      const safeTaskName = (heading || "task").replace(/\s+/g, "_").replace(/[^\w\-]+/g, "");
      const today = dayjs().format("YYYY-MM-DD");
      downloadCsvFile(`${safeProjectName}_${safeTaskName}_customer_${today}.csv`, csvString);
    } catch (err) {
      console.error("Customer task economy export failed", err);
      setEconomyExportError(t("projectDetails.exportFailed") || "Export failed");
    } finally {
      setCustomerTaskCsvExportKey(null);
    }
  }

  function closeEconomyExpectedCostsModal() {
    setEconomyExpectedCostsModal({
      open: false,
      heading: "",
      loading: false,
      error: null,
      accruals: [],
      customerView: false,
      taskContext: null,
    });
  }

  function refetchEconomyAfterAccrualChange() {
    if (economyView === "per_task") fetchEconomyByTask();
    else if (economyView === "customer") fetchEconomyByCustomer();
  }

  async function handleExpectedCostSettle(accrualId) {
    if (!id || !window.confirm(t("projectDetails.expectedCostsSettleConfirm"))) return;
    setExpectedCostActionId(accrualId);
    try {
      await axios.post(`/projects/${id}/material-accruals/${accrualId}/settle`, {});
      refetchEconomyAfterAccrualChange();
      setEconomyExpectedCostsModal((s) => ({
        ...s,
        accruals: (s.accruals || []).filter((a) => (a.accrualId || a._id) !== accrualId),
      }));
    } catch (err) {
      alert(err?.response?.data?.message || t("projectDetails.expectedCostsSettleError"));
    } finally {
      setExpectedCostActionId(null);
    }
  }

  async function handleExpectedCostDelete(accrualId) {
    if (!id || !window.confirm(t("projectDetails.expectedCostsDeleteConfirm"))) return;
    setExpectedCostActionId(accrualId);
    try {
      await axios.delete(`/projects/${id}/material-accruals/${accrualId}`);
      refetchEconomyAfterAccrualChange();
      setEconomyExpectedCostsModal((s) => ({
        ...s,
        accruals: (s.accruals || []).filter((a) => (a.accrualId || a._id) !== accrualId),
      }));
    } catch (err) {
      alert(err?.response?.data?.message || t("projectDetails.expectedCostsDeleteError"));
    } finally {
      setExpectedCostActionId(null);
    }
  }

  function handleExpectedCostEdit(accrual) {
    setEditingExpectedCost(accrual);
    setExpectedCostFormModalOpen(true);
  }

  function openAddExpectedCostModal() {
    setEditingExpectedCost(null);
    setExpectedCostFormModalOpen(true);
  }

  // Economy date range presets: row 1 = Last 30 days (left), Since project start (right); row 2 = This month, This year
  const economyPresets = useMemo(() => [
    {
      id: "last-30-days",
      label: "Last 30 days",
      getRange: () => ({
        from: dayjs().subtract(29, "day").startOf("day").toDate(),
        to: dayjs().endOf("day").toDate(),
      }),
    },
    {
      id: "since-project-start",
      label: "Since project start",
      disabled: !project?.startDate,
      getRange: () => ({
        from: project?.startDate ? new Date(project.startDate) : dayjs().startOf("month").toDate(),
        to: dayjs().endOf("day").toDate(),
      }),
    },
    {
      id: "this-month",
      label: "This month",
      getRange: () => ({
        from: dayjs().startOf("month").toDate(),
        to: dayjs().endOf("month").toDate(),
      }),
    },
    {
      id: "this-year",
      label: "This year",
      getRange: () => ({
        from: dayjs().startOf("year").toDate(),
        to: dayjs().endOf("year").toDate(),
      }),
    },
  ], [project?.startDate]);

  useEffect(() => {
    if (!photosDownloading) return undefined;
    pushModalScrollLock();
    return () => popModalScrollLock();
  }, [photosDownloading]);

  useEffect(() => {
    if (!id || !user?.isAdmin) return;
    if (economyView === "per_task") fetchEconomyByTask();
    if (economyView === "customer") fetchEconomyByCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, economyView]);

  // Lazy-load economy when economy section enters viewport (IntersectionObserver)
  useEffect(() => {
    if (!id || !user?.isAdmin || !economySectionRef.current) return;
    const el = economySectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          fetchEconomy();
          observer.disconnect();
        }
      },
      { rootMargin: "200px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.isAdmin]);

  // Export project reports as CSV (uses existing server endpoint)
  const openProjectExportModal = () => {
    setProjectExportStartDate(dayjs().subtract(3, "month").format("YYYY-MM-DD"));
    setProjectExportModalOpen(true);
  };

  const closeProjectExportModal = () => {
    if (projectExportSubmitting) return;
    setProjectExportModalOpen(false);
  };

  const openEconomyExportModal = () => {
    setEconomyExportStartDate(dayjs().subtract(3, "month").format("YYYY-MM-DD"));
    setEconomyExportModalOpen(true);
  };

  const closeEconomyExportModal = () => {
    if (economyExportSubmitting) return;
    setEconomyExportModalOpen(false);
  };

  const handleExportProjectCSV = async (startDate = null) => {
    if (!id) return;
    setProjectExportSubmitting(true);
    try {
      const endpoint = `/exports/reports/project/${id}`;
      const params = {};
      if (startDate) {
        params.startDate = startDate;
        params.endDate = dayjs().format("YYYY-MM-DD");
      }
      if (timeReportMarkers.length) {
        params.has = timeReportMarkers.join(",");
      }
      const resp = await axios.get(endpoint, { params, responseType: "blob" });
      setExportError("");
      const blob = new Blob([resp.data], { type: "text/csv" });
      const today = dayjs().format("YYYY-MM-DD");
      const filename = `${project?.name || "project"}_reports_${startDate || "all"}_${today}.csv`;
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setProjectExportModalOpen(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        setExportError(t("projectDetails.exportNoReportsFound"));
      } else {
        console.error("Export failed", err);
        setExportError("Export failed");
      }
    } finally {
      setProjectExportSubmitting(false);
    }
  };

  // Export the currently visible economy table to CSV
  const handleExportEconomyCSV = async (startDate = null) => {
    if (!id || !user?.isAdmin) return;
    setEconomyExportError("");
    if (startDate) setEconomyExportSubmitting(true);

    const escapeCsv = (value) => {
      if (value == null) return "";
      const s = String(value);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const downloadCsv = (filename, csvString) => {
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    try {
      if (economyView === "per_worker") {
        let economyData = economy;
        if (startDate) {
          const endDate = dayjs().format("YYYY-MM-DD");
          const res = await axios.get(`/projects/${id}/economy`, {
            params: { startDate, endDate },
          });
          economyData = res.data;
          setEconomy(res.data);
          setEconomyDateRange({
            from: dayjs(startDate).startOf("day").toDate(),
            to: dayjs().endOf("day").toDate(),
          });
        }
        if (!economyData || !economyData.workers) return;
        const headers = [
          t("projectDetails.worker"),
          t("projectDetails.hours"),
          t("projectDetails.cost"),
          t("projectDetails.billed"),
          t("projectDetails.margin"),
          t("projectDetails.marginPercent"),
        ];

        const rows = economyData.workers.map((w) => {
          const workerIdStr = w.workerId?.toString?.() ?? w.workerId;
          const allTimeWorker = economyData.allTime?.workers?.find(
            (x) => (x.workerId?.toString?.() ?? x.workerId) === workerIdStr
          );

          const primaryHours = w.totalHours != null ? w.totalHours : "N/A";
          const hours = allTimeWorker?.totalHours != null ? `${primaryHours} (${allTimeWorker.totalHours} ${t("projectDetails.total")})` : primaryHours;

          const primaryCost = w.companyCost != null ? `${Math.round(w.companyCost).toLocaleString("sv-SE")} kr` : "N/A";
          const secondaryCost = allTimeWorker?.companyCost != null ? `${Math.round(allTimeWorker.companyCost).toLocaleString("sv-SE")} kr ${t("projectDetails.total")}` : null;
          const cost = secondaryCost ? `${primaryCost} (${secondaryCost})` : primaryCost;

          const primaryBilled = w.billed != null ? `${Math.round(w.billed).toLocaleString("sv-SE")} kr` : "N/A";
          const secondaryBilled = allTimeWorker?.billed != null ? `${Math.round(allTimeWorker.billed).toLocaleString("sv-SE")} kr ${t("projectDetails.total")}` : null;
          const billed = secondaryBilled ? `${primaryBilled} (${secondaryBilled})` : primaryBilled;

          const primaryMargin = w.margin != null ? `${Math.round(w.margin).toLocaleString("sv-SE")} kr` : "N/A";
          const secondaryMargin = allTimeWorker?.margin != null ? `${Math.round(allTimeWorker.margin).toLocaleString("sv-SE")} kr ${t("projectDetails.total")}` : null;
          const margin = secondaryMargin ? `${primaryMargin} (${secondaryMargin})` : primaryMargin;

          const primaryMarginPct = w.marginPercent != null ? `${w.marginPercent.toFixed(1)}%` : "N/A";
          const secondaryMarginPct =
            allTimeWorker?.marginPercent != null ? `${allTimeWorker.marginPercent.toFixed(1)}% ${t("projectDetails.total")}` : null;
          const marginPct = secondaryMarginPct ? `${primaryMarginPct} (${secondaryMarginPct})` : primaryMarginPct;

          return [w.workerName, hours, cost, billed, margin, marginPct];
        });

        // Footer totals row (matches the UI footer values)
        const primaryTotalHours = economyData.workers.reduce((sum, w) => sum + (w.totalHours || 0), 0);
        const secondaryTotalHours = economyData.allTime?.workers?.reduce(
          (sum, w) => sum + (w.totalHours || 0),
          0
        );
        const hoursTotal = economyData.allTime?.workers ? `${primaryTotalHours} (${secondaryTotalHours} ${t("projectDetails.total")})` : primaryTotalHours;

        const costTotalPrimary =
          economyData.totals?.totalCompanyCost != null ? `${Math.round(economyData.totals.totalCompanyCost).toLocaleString("sv-SE")} kr` : "N/A";
        const costTotalSecondary = economyData.allTime?.totals?.totalCompanyCost != null ? `${Math.round(economyData.allTime.totals.totalCompanyCost).toLocaleString("sv-SE")} kr ${t("projectDetails.total")}` : null;
        const costTotal = costTotalSecondary ? `${costTotalPrimary} (${costTotalSecondary})` : costTotalPrimary;

        const billedTotalPrimary =
          economyData.totals?.totalBilled != null ? `${Math.round(economyData.totals.totalBilled).toLocaleString("sv-SE")} kr` : "N/A";
        const billedTotalSecondary = economyData.allTime?.totals?.totalBilled != null ? `${Math.round(economyData.allTime.totals.totalBilled).toLocaleString("sv-SE")} kr ${t("projectDetails.total")}` : null;
        const billedTotal = billedTotalSecondary ? `${billedTotalPrimary} (${billedTotalSecondary})` : billedTotalPrimary;

        const marginTotalPrimary =
          economyData.totals?.totalMargin != null ? `${Math.round(economyData.totals.totalMargin).toLocaleString("sv-SE")} kr` : "N/A";
        const marginTotalSecondary = economyData.allTime?.totals?.totalMargin != null ? `${Math.round(economyData.allTime.totals.totalMargin).toLocaleString("sv-SE")} kr ${t("projectDetails.total")}` : null;
        const marginTotal = marginTotalSecondary ? `${marginTotalPrimary} (${marginTotalSecondary})` : marginTotalPrimary;

        const marginPctTotalPrimary =
          economyData.totals?.totalMarginPercent != null ? `${economyData.totals.totalMarginPercent.toFixed(1)}%` : "N/A";
        const marginPctTotalSecondary =
          economyData.allTime?.totals?.totalMarginPercent != null
            ? `${economyData.allTime.totals.totalMarginPercent.toFixed(1)}% ${t("projectDetails.total")}`
            : null;
        const marginPctTotal = marginPctTotalSecondary
          ? `${marginPctTotalPrimary} (${marginPctTotalSecondary})`
          : marginPctTotalPrimary;

        rows.push([t("projectDetails.totalLabel"), hoursTotal, costTotal, billedTotal, marginTotal, marginPctTotal]);

        const csvString = [headers.map(escapeCsv).join(","), ...rows.map((r) => r.map(escapeCsv).join(","))].join("\n");
        downloadCsv(
          `${project?.name || "project"}_economy_${economyView}_${new Date().toISOString().slice(0, 10)}.csv`,
          csvString
        );
        if (startDate) setEconomyExportModalOpen(false);
        return;
      }

      if (economyView === "per_task") {
        if (!economyByTask || !economyByTask.tasks) return;

        const formatKr = (v) => (v != null && Number(v) > 0 ? `${Math.round(Number(v)).toLocaleString("sv-SE")} kr` : "—");
        const formatMarginCsv = (margin) => (margin != null ? formatEconomyMarginKr(margin) : "—");
        const formatMarginPctCsv = (marginPct) => (marginPct != null ? `${marginPct.toFixed(1)}%` : "—");

        const headers = [
          t("projectDetails.taskSubtask"),
          t("projectDetails.reportedHours"),
          t("projectDetails.labourCost"),
          t("projectDetails.materialsAndSubs"),
          t("projectDetails.expectedCostsColumn"),
          t("projectDetails.billingModelCol"),
          t("projectDetails.estimatedHours"),
          t("projectDetails.estimatedCost"),
          t("projectDetails.margin"),
          t("projectDetails.marginPercent"),
        ];

        const rows = [];
        economyByTask.tasks.forEach((task) => {
          const formatCsvStacked = (atValue, totalValue, format) => {
            const primary = format(atValue);
            const total = format(totalValue);
            return primary === total ? primary : `${primary}\n${total}`;
          };

          const pushTaskRow = (row, isSubtask) => {
            const margins = isSubtask ? null : perTaskMargins(row);
            const rowMargin = isSubtask ? perTaskMarginForRow(row) : null;
            const priceParts = isSubtask ? null : perTaskPriceParts(row);
            const billingLabel = row.includedInParent
              ? t("projectDetails.includedInParentTask")
              : row.billingModel
                ? String(row.billingModel).replace("_", " ")
                : "—";
            const rollups = !isSubtask ? getEconomyTaskRollups(row) : null;
            const fmtHours = (v) => (v != null && v > 0 ? v : "—");
            const hoursCol =
              rollups?.hasSubtasks
                ? formatCsvStacked(
                    rollups.atTaskLevel.reportedHours,
                    rollups.includingSubtasks.reportedHours,
                    fmtHours
                  )
                : fmtHours(row.reportedHours);
            const labourCol =
              rollups?.hasSubtasks
                ? formatCsvStacked(rollups.atTaskLevel.labourCost, rollups.includingSubtasks.labourCost, formatKr)
                : formatKr(row.labourCost);
            const materialsCol =
              rollups?.hasSubtasks
                ? formatCsvStacked(
                    rollups.atTaskLevel.materialsFromInvoices ?? rollups.atTaskLevel.materialsAndSubs,
                    rollups.includingSubtasks.materialsFromInvoices ?? rollups.includingSubtasks.materialsAndSubs,
                    formatKr
                  )
                : formatKr(row.materialsFromInvoices ?? row.materialsAndSubs);
            const expectedCol =
              rollups?.hasSubtasks
                ? formatCsvStacked(
                    rollups.atTaskLevel.materialsFromAccruals,
                    rollups.includingSubtasks.materialsFromAccruals,
                    formatKr
                  )
                : formatKr(row.materialsFromAccruals ?? 0);

            rows.push([
              `${isSubtask ? "- " : ""}${displayText(row, "title") || row.title || "—"}`,
              hoursCol,
              labourCol,
              materialsCol,
              expectedCol,
              billingLabel,
              row.estimatedHours != null ? row.estimatedHours : "—",
              priceParts?.hasSubtasks
                ? formatCsvStacked(priceParts.own, priceParts.total, formatKr)
                : formatKr(row.estimatedCost),
              margins?.hasSubtasks
                ? formatCsvStacked(margins.own.margin, margins.total.margin, formatMarginCsv)
                : formatMarginCsv(rowMargin?.margin ?? margins?.own.margin),
              margins?.hasSubtasks
                ? formatCsvStacked(margins.own.marginPct, margins.total.marginPct, formatMarginPctCsv)
                : formatMarginPctCsv(rowMargin?.marginPct ?? margins?.own.marginPct),
            ]);
          };

          pushTaskRow(task, false);
          (task.subtasks || []).forEach((sub) => pushTaskRow(sub, true));
        });

        const totals = sumDisplayedPerTaskTotals(economyByTask.tasks);
        const totalMarginPct = totals.price > 0 ? (totals.margin / totals.price) * 100 : null;

        rows.push([
          t("projectDetails.totalLabel"),
          totals.hours > 0 ? totals.hours : "—",
          formatKr(totals.labour),
          formatKr(totals.materials),
          formatKr(totals.expected),
          "—",
          "—",
          totals.price > 0 ? formatKr(totals.price) : totals.hasMargin ? "0 kr" : "—",
          totals.hasMargin ? formatEconomyMarginKr(totals.margin) : "—",
          totalMarginPct != null ? `${totalMarginPct.toFixed(1)}%` : "—",
        ]);

        const csvString = [headers.map(escapeCsv).join(","), ...rows.map((r) => r.map(escapeCsv).join(","))].join("\n");
        downloadCsv(
          `${project?.name || "project"}_economy_${economyView}_${new Date().toISOString().slice(0, 10)}.csv`,
          csvString
        );
        return;
      }

      if (economyView === "customer") {
        if (!economyByCustomer || !economyByCustomer.tasks) return;

        const formatKr = (v) => (v != null && Number(v) > 0 ? `${Math.round(Number(v)).toLocaleString("sv-SE")} kr` : "—");
        const formatKrOrZero = (v) =>
          v != null && Number(v) > 0 ? `${Math.round(Number(v)).toLocaleString("sv-SE")} kr` : "0 kr";
        const addVat = (v) => Number(v ?? 0) * 1.25;

        const scaleMaterials = (v) => Math.ceil(Number(v ?? 0) * 1.15 * 1.25);
        const formatMaterialsAndSubs = (v) => {
          const scaled = scaleMaterials(v);
          return scaled > 0 ? `${scaled.toLocaleString("sv-SE")} kr` : "0 kr";
        };

        const getEstimatedCostInclVat = (v) =>
          v != null && Number(v) > 0 ? addVat(v) : null;
        const getActualCost = (row) => addVat(row.billedAmount) + scaleMaterials(row.materialsAndSubs);
        const getActualCostIncludingSubtasks = (task) =>
          getActualCost(task) + (task.subtasks || []).reduce((s, sub) => s + getActualCost(sub), 0);

        const getEstimatedCostDisplay = (row, actualTotalCost) => {
          const estimatedInclVat = getEstimatedCostInclVat(row.estimatedCost);
          const main = estimatedInclVat != null ? formatKr(estimatedInclVat) : "—";
          const actual = formatKrOrZero(actualTotalCost);
          return `${main} / ${actual}`;
        };

        const headers = [
          t("projectDetails.taskSubtask"),
          t("projectDetails.reportedHours"),
          t("projectDetails.costInclVat"),
          t("projectDetails.materialsAndSubsInclVat"),
          t("projectDetails.expectedCostsColumnInclVat"),
          t("projectDetails.estimatedHours"),
          t("projectDetails.estimatedCostInclVat"),
        ];

        const formatCsvStacked = (atValue, totalValue, format) => {
          const primary = format(atValue);
          const total = format(totalValue);
          return primary === total ? primary : `${primary}\n${total}`;
        };
        const fmtHours = (v) => (v != null && v > 0 ? v : "—");

        const rows = [];
        economyByCustomer.tasks.forEach((task) => {
          const rollups = getEconomyTaskRollups(task);
          const displayed = getCustomerEconomyDisplayedParts(task);
          rows.push([
            `${displayText(task, "title") || task.title || "—"}`,
            rollups.hasSubtasks
              ? formatCsvStacked(displayed.own.reportedHours, displayed.total.reportedHours, fmtHours)
              : fmtHours(task.reportedHours),
            rollups.hasSubtasks
              ? formatCsvStacked(
                  displayed.own.costInclVat,
                  displayed.total.costInclVat,
                  formatCustomerEconomyDisplayedKr
                )
              : formatKrOrZero(addVat(task.billedAmount)),
            rollups.hasSubtasks
              ? formatCsvStacked(
                  displayed.own.materialsInclVat,
                  displayed.total.materialsInclVat,
                  formatCustomerEconomyDisplayedKr
                )
              : formatMaterialsAndSubs(task.materialsFromInvoices ?? task.materialsAndSubs),
            rollups.hasSubtasks
              ? formatCsvStacked(
                  displayed.own.expectedInclVat,
                  displayed.total.expectedInclVat,
                  formatCustomerEconomyDisplayedKr
                )
              : formatMaterialsAndSubs(task.materialsFromAccruals ?? 0),
            task.estimatedHours != null ? task.estimatedHours : "—",
            getEstimatedCostDisplay(task, getActualCostIncludingSubtasks(task)),
          ]);

          (task.subtasks || []).forEach((sub) => {
            rows.push([
              `- ${displayText(sub, "title") || sub.title || "—"}`,
              sub.reportedHours != null && sub.reportedHours > 0 ? sub.reportedHours : "—",
              formatKrOrZero(addVat(sub.billedAmount)),
              formatMaterialsAndSubs(sub.materialsFromInvoices ?? sub.materialsAndSubs),
              formatMaterialsAndSubs(sub.materialsFromAccruals ?? 0),
              sub.estimatedHours != null ? sub.estimatedHours : "—",
              getEstimatedCostDisplay(sub, getActualCost(sub)),
            ]);
          });
        });

        const displayedTotals = sumCustomerEconomyDisplayedTaskTotals(economyByCustomer.tasks);
        const totals = economyByCustomer.tasks.reduce(
          (acc, tsk) => ({
            estimatedCost: acc.estimatedCost + Number(tsk.estimatedCost ?? 0) + (tsk.subtasks || []).reduce((s, st) => s + Number(st.estimatedCost ?? 0), 0),
          }),
          { estimatedCost: 0 }
        );
        const totalActualCost = economyByCustomer.tasks.reduce(
          (sum, t) => sum + getActualCostIncludingSubtasks(t),
          0
        );
        const totalEstimatedCostDisplay = getEstimatedCostDisplay(
          { estimatedCost: totals.estimatedCost },
          totalActualCost
        );

        rows.push([
          t("projectDetails.totalLabel"),
          displayedTotals.reportedHours > 0 ? displayedTotals.reportedHours : "—",
          formatCustomerEconomyDisplayedKr(displayedTotals.costInclVat),
          formatCustomerEconomyDisplayedKr(displayedTotals.materialsInclVat),
          formatCustomerEconomyDisplayedKr(displayedTotals.expectedInclVat),
          "—",
          totalEstimatedCostDisplay,
        ]);

        const csvString = [headers.map(escapeCsv).join(","), ...rows.map((r) => r.map(escapeCsv).join(","))].join("\n");
        downloadCsv(
          `${project?.name || "project"}_economy_${economyView}_${new Date().toISOString().slice(0, 10)}.csv`,
          csvString
        );
        return;
      }
    } catch (err) {
      console.error("Economy export failed", err);
      setEconomyExportError("Export failed");
    } finally {
      if (startDate) setEconomyExportSubmitting(false);
    }
  };

  // Helpers for image transformations and modal handling
  const injectTransformation = (originalUrl, transformation) => {
    if (!originalUrl) return originalUrl;
    try {
      const idx = originalUrl.indexOf("/upload/");
      if (idx === -1) return originalUrl;
      const before = originalUrl.substring(0, idx + 8); // include 'upload/'
      const after = originalUrl.substring(idx + 8);
      return before + transformation + after;
    } catch (e) {
      return originalUrl;
    }
  };

  const getThumbUrl = (url) =>
    injectTransformation(url, "c_fill,g_auto,w_240,h_240,q_auto,f_auto/");

  const getViewUrl = (url) =>
    injectTransformation(url, "c_limit,w_1600,q_auto,f_auto/");

  const getCoverImageUrl = (url) => {
    if (!url) return null;
    // Use a larger thumbnail for hero cover images: 1200x400 (3:1 aspect ratio)
    return injectTransformation(url, "c_fill,g_auto,w_1200,h_400,q_auto,f_auto/");
  };

  const preferredLanguage = user?.languages?.ui || "English";
  const getDisplayDescription = (photo) =>
    getDisplayTextFromTranslations(
      photo.translatedDescriptions,
      photo.descriptionLanguage,
      photo.description,
      preferredLanguage,
      photo.descriptionDisplay
    );

  const openPhotoModalAt = (idx) => {
    // Transform photos to include translated descriptions
    const photosWithTranslations = photos.map((p) => ({
      ...p,
      description: getDisplayDescription(p),
    }));
    setPhotoModal({
      isOpen: true,
      items: photosWithTranslations,
      index: idx || 0,
    });
  };

  const closePhotoModal = () =>
    setPhotoModal({ isOpen: false, items: [], index: 0 });

  const loadMorePhotos = () => {
    if (photosLoading || !photosHasMore) return;
    // Store current photo count before loading more
    setPreviousPhotoCount(photos.length);
    fetchPhotos(photosPage + 1);
  };

  // Scroll to show first newly loaded photo at top of viewport (below TopBar)
  useEffect(() => {
    if (
      previousPhotoCount > 0 &&
      photos.length > previousPhotoCount &&
      photosContainerRef.current
    ) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        // Find the first new photo element by its data attribute
        const firstNewPhoto = photosContainerRef.current?.querySelector(
          `[data-photo-index="${previousPhotoCount}"]`
        );
        if (firstNewPhoto) {
          const topBarHeight = 56; // h-14 = 56px
          const elementTop = firstNewPhoto.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementTop - topBarHeight - 16, // 16px extra padding
            behavior: "smooth",
          });
        }
        // Reset the previous count
        setPreviousPhotoCount(0);
      }, 100);
    }
  }, [photos.length, previousPhotoCount]);

  // Scroll to show first newly loaded time report at top of viewport (below TopBar)
  useEffect(() => {
    if (
      previousTimeReportsCount > 0 &&
      timeReports.length > previousTimeReportsCount &&
      timeReportsContainerRef.current
    ) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        // Find the first new report element by its data attribute
        const firstNewReport = timeReportsContainerRef.current?.querySelector(
          `[data-report-index="${previousTimeReportsCount}"]`
        );
        if (firstNewReport) {
          const topBarHeight = 56; // h-14 = 56px
          const elementTop = firstNewReport.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementTop - topBarHeight - 16, // 16px extra padding
            behavior: "smooth",
          });
        }
        // Reset the previous count
        setPreviousTimeReportsCount(0);
      }, 100);
    }
  }, [timeReports.length, previousTimeReportsCount]);

  // Download all photos as zip (with confirm and progress). Supports single archive or multi-part archives.
  const downloadAllPhotos = async () => {
    if (!id) return;

    try {
      // 1) get count / quick urls to compute X
      const countResp = await axios.get(`/projects/${id}/photos/download`);
      const urls = countResp.data.urls || [];
      const total = urls.length;

      if (total === 0) {
        alert(t("projectDetails.noPhotosToDownload"));
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to download ${total} photos?`
      );
      if (!confirmed) return;

      setPhotosDownloading(true);
      setPhotosDownloadProgress({ loaded: 0, total: 0, percent: 0 });
      setPhotosDownloadPart(0);
      setPhotosDownloadTotalParts(0);

      const controller = new AbortController();
      photosDownloadControllerRef.current = controller;

      // 2) request server zip endpoint (disable cache to avoid 304 responses)
      // Use our shared axios instance so the Authorization token + interceptors are applied.
      // axios supports AbortController via the `signal` option and onDownloadProgress for progress.
      let resp = await axios.get(`/projects/${id}/photos/download-zip`, {
        signal: controller.signal,
        responseType: "arraybuffer",
        headers: { Accept: "application/zip, application/json, */*" },
        onDownloadProgress: (ev) => {
          const loaded = ev.loaded || 0;
          const total = ev.total || 0;
          const percent = total ? Math.round((loaded / total) * 100) : 0;
          setPhotosDownloadProgress({ loaded, total, percent });
        },
      });

      // If we unexpectedly get a 304 (Not Modified) from the dev server/proxy, retry once with a cache-buster.
      if (resp.status === 304) {
        resp = await axios.get(
          `/projects/${id}/photos/download-zip?_=${Date.now()}`,
          {
            signal: controller.signal,
            responseType: "arraybuffer",
            headers: { Accept: "application/zip, application/json, */*" },
            onDownloadProgress: (ev) => {
              const loaded = ev.loaded || 0;
              const total = ev.total || 0;
              const percent = total ? Math.round((loaded / total) * 100) : 0;
              setPhotosDownloadProgress({ loaded, total, percent });
            },
          }
        );
      }

      // axios returns status / headers / data. Handle zip (arraybuffer) or JSON (encoded as arraybuffer).
      const status = resp.status;
      if (status >= 200 && status < 300) {
        const headers = resp.headers || {};
        const contentType =
          (headers["content-type"] || headers["Content-Type"] || "") + "";
        // If server sent a zip directly
        if (contentType.includes("application/zip")) {
          const blob = new Blob([resp.data], { type: "application/zip" });
          // Try to derive filename from header
          let filename = `${project?.name || "project"}_Photos_${new Date()
            .toISOString()
            .slice(0, 10)}.zip`;
          const cd =
            headers["content-disposition"] || headers["Content-Disposition"];
          if (cd) {
            const m = String(cd).match(/filename="?([^";]+)"?/);
            if (m && m[1]) filename = m[1];
          }
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
          setPhotosDownloading(false);
          return;
        }

        // Otherwise try to parse JSON response encoded as arraybuffer
        let json = null;
        try {
          const text = new TextDecoder().decode(resp.data);
          json = JSON.parse(text);
        } catch (e) {
          json = null;
        }

        if (json) {
          // If server provided a direct URL to download (signed Cloudinary URL), let the browser handle it
          if (json.url) {
            // Open in new tab to let browser download (preserves credentials on direct cloud URL)
            window.open(json.url, "_blank", "noopener,noreferrer");
            setPhotosDownloading(false);
            return;
          }

          // If server returned parts array with URLs (multi-part)
          if (Array.isArray(json.parts) && json.parts.length) {
            setPhotosDownloadTotalParts(json.parts.length);
            for (let i = 0; i < json.parts.length; i++) {
              const part = json.parts[i];
              setPhotosDownloadPart(i + 1);
              // trigger download by creating a link (avoids fetch/CORS)
              const link = document.createElement("a");
              link.href = part.url;
              link.download =
                part.filename ||
                `${project?.name || "project"}_Photos_part${i + 1}.zip`;
              document.body.appendChild(link);
              link.click();
              link.remove();
            }
            // Reset progress state
            setPhotosDownloading(false);
            setPhotosDownloadPart(0);
            setPhotosDownloadTotalParts(0);
            setPhotosDownloadProgress({ loaded: 0, total: 0, percent: 0 });
            return;
          }
        }

        throw new Error("Unexpected response from server");
      } else {
        // non-2xx
        const bodyText =
          resp && resp.data
            ? typeof resp.data === "string"
              ? resp.data
              : (() => {
                  try {
                    return JSON.stringify(resp.data);
                  } catch (e) {
                    return String(resp.data);
                  }
                })()
            : resp.statusText || "";
        throw new Error(
          `Photo download endpoint returned ${status}: ${bodyText}`
        );
      }

      if (!json || (!json.parts && !json.url)) {
        throw new Error("Unexpected response from server");
      }

      // If server returned direct parts array
      const parts =
        json.parts ||
        (json.url
          ? [
              {
                url: json.url,
                filename: json.filename || `${project?.name}_Photos.zip`,
              },
            ]
          : []);
      setPhotosDownloadTotalParts(parts.length);

      // Download parts sequentially
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        setPhotosDownloadPart(i + 1);
        // fetch part with progress
        const pResp = await fetch(part.url, { signal: controller.signal });
        if (!pResp.ok) throw new Error("Part download failed");
        const totalLen = parseInt(
          pResp.headers.get("content-length") || "0",
          10
        );
        setPhotosDownloadProgress({ loaded: 0, total: totalLen, percent: 0 });
        const reader = pResp.body.getReader();
        const chunks = [];
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          const percent = totalLen
            ? Math.round((received / totalLen) * 100)
            : 0;
          setPhotosDownloadProgress({
            loaded: received,
            total: totalLen,
            percent,
          });
        }
        const blob = new Blob(chunks, { type: "application/zip" });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download =
          part.filename ||
          `${project?.name || "project"}_Photos_part${i + 1}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      }

      // Done
      setPhotosDownloading(false);
      setPhotosDownloadPart(0);
      setPhotosDownloadTotalParts(0);
      setPhotosDownloadProgress({ loaded: 0, total: 0, percent: 0 });
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn("Download aborted");
        setError(t("projectDetails.downloadCancelled"));
      } else {
        console.error("Download all photos failed", err);
        setError(t("projectDetails.failedToDownloadPhotos"));
      }
      setPhotosDownloading(false);
    } finally {
      photosDownloadControllerRef.current = null;
    }
  };

  useEffect(() => {
    if (!isNew && id) {
      // Record that this project was viewed (for sorting in list view)
      recordProjectView(id);
      
      // load project metadata, photos, time reports (economy is lazy-loaded via IntersectionObserver)
      fetchProject().then(() => {
        fetchPhotos(1);
        fetchTimeReports(true);
      });
    }
    // Fetch users for worker rates dropdown when in edit mode
    if (isEditPath && canEdit) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.pathname]);

  // Fetch project task bundle for view-mode tasks section (All tasks tree)
  useEffect(() => {
    if (!id || isNew || isEditPath) {
      setProjectTaskBundle(null);
      return;
    }
    let cancelled = false;
    setProjectTaskBundleLoading(true);
    axios
      .get(`/task-bundles/projects/${id}/task-bundle`)
      .then((res) => {
        if (!cancelled) setProjectTaskBundle(res.data);
      })
      .catch((err) => {
        if (err.response?.status !== 404 && !cancelled) {
          console.error("Error fetching project task bundle:", err);
        }
        if (!cancelled) setProjectTaskBundle(null);
      })
      .finally(() => {
        if (!cancelled) setProjectTaskBundleLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, isNew, isEditPath]);

  /** Parent task label for time report rows when the entry is a subtask (bundle lookup). */
  const parentTaskLabelForTimeEntry = (entry) => {
    if (!entry?.subtaskId || !projectTaskBundle?.tasks?.length) return null;
    const task = projectTaskBundle.tasks.find(
      (t) => t._id != null && entry.taskId != null && t._id.toString() === entry.taskId.toString()
    );
    if (!task) return null;
    return displayText(task, "title") || task.title || null;
  };

  const fetchProject = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/projects/${id}`);
      setProject(res.data);
      if (res.data.driveFolderId) {
        // If the project returned from the server includes a linked Drive folder
        // fetch the Drive files immediately. We pass the project _id to the
        // fetchDriveFiles helper because `project` state won't be updated until
        // after this function completes.
        await fetchDriveFiles({}, res.data._id);
      }
      setForm({
        name: res.data.name || "",
        address: res.data.address || "",
        zipCode: res.data.zipCode || "",
        city: res.data.city || "",
        startDate: res.data.startDate
          ? res.data.startDate.substring(0, 10)
          : "",
        endDate: res.data.endDate ? res.data.endDate.substring(0, 10) : "",
        status: res.data.status || "",
        description: res.data.description || "",
        manager: res.data.manager ? res.data.manager._id : "",
        billingModel: res.data.billingModel || "",
        hourlyRate: res.data.hourlyRate || "",
        fixedPrice: res.data.fixedPrice || "",
        showTasksInTimeReport: res.data.showTasksInTimeReport !== false, // default to true
      });
      // Load existing worker rates
      if (res.data.workerRates && Array.isArray(res.data.workerRates)) {
        setWorkerRates(res.data.workerRates);
      } else {
        setWorkerRates([]);
      }
    } catch (err) {
      console.error("Failed to fetch project", err);
      setError(t("projectDetails.failedToLoadProject"));
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (e) => {
    e && e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      // Remove empty-string/null fields to satisfy server validators (e.g. MongoId/date checks)
      const payload = Object.entries(form).reduce((acc, [k, v]) => {
        if (v === "" || v === null || typeof v === "undefined") return acc;
        acc[k] = v;
        return acc;
      }, {});
      // Include worker rates in payload
      payload.workerRates = workerRates;
      if (isNew) {
        const res = await axios.post("/projects/create", payload);
        navigate(`/projects/${res.data._id}`);
      } else {
        await axios.put(`/projects/${id}`, payload);
        // reload project
        await fetchProject();
        navigate(`/projects/${id}`);
      }
    } catch (err) {
      console.error("Save failed", err);
      const serverMsg =
        err?.response?.data?.message ||
        (err?.response?.data?.errors
          ? JSON.stringify(err.response.data.errors)
          : null);
      setError(serverMsg || err.message || t("projectDetails.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const onDeleteProject = async () => {
    if (!canEdit) return;
    if (!confirm("Soft-delete this project?")) return;
    try {
      await axios.delete(`/projects/${id}`);
      navigate("/projects");
    } catch (err) {
      console.error("Delete failed", err);
      setError(t("projectDetails.deleteFailed"));
    }
  };

  // File upload flow: get presigned, upload, then save metadata
  const uploadFile = async (file) => {
    if (!file || !project) return;
    try {
      // 1) request upload url

      const res = await axios.post(`/projects/${project._id}/upload-url`, {
        filename: file.name,
        contentType: file.type,
      });
      const { url, key } = res.data;
      // 2) upload to S3 (PUT)
      await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      // 3) save metadata

      const addRes = await axios.post(`/projects/${project._id}/files`, {
        filename: key,
        originalName: file.name,
        mimeType: file.type,
        size: file.size,
        url: url.split("?")[0], // store durable url (without query) if desired
        key,
      });
      setProject((p) => ({ ...p, files: [...p.files, addRes.data] }));
    } catch (err) {
      console.error("Upload failed", err);
      setError(t("projectDetails.uploadFailed"));
    }
  };

  const onFileInput = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) uploadFile(f);
    e.target.value = "";
  };

  const deleteFile = async (fileId) => {
    if (!canEdit) return;
    if (!confirm("Delete file? This will remove the file and metadata."))
      return;

    try {
      await axios.delete(`/projects/${project._id}/files/${fileId}`);
      setProject((p) => ({
        ...p,
        files: p.files.filter((f) => f._id !== fileId),
      }));
    } catch (err) {
      console.error("Delete file failed", err);
      setError(t("projectDetails.failedToDeleteFile"));
    }
  };

  const downloadFile = async (file) => {
    if (!file || !project) return;
    try {
      const res = await axios.get(
        `/projects/${project._id}/files/${file._id}/download`
      );
      const url = res?.data?.url;
      if (url) {
        // Try to fetch the file and trigger a download without opening a new tab.
        // If that fails (CORS or other), fall back to opening the URL.
        try {
          const fileRes = await fetch(url, { method: "GET" });
          if (!fileRes.ok) throw new Error("Failed to download file");
          const blob = await fileRes.blob();
          const filename = file.originalName || file.filename || "download";
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
          console.warn(
            "Blob download failed, falling back to opening URL",
            err
          );
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } else {
        setError(t("projectDetails.downloadUrlNotAvailable"));
      }
    } catch (err) {
      console.error("Download failed", err);
      setError(t("projectDetails.failedToGetDownloadUrl"));
    }
  };

  // --- Google Drive actions ---
  const ensureDriveFolder = async () => {
    if (!project) return;
    try {
      await axios.post(`/projects/${project._id}/drive/ensure-folder`);
      // refresh project metadata (folder id saved server-side)
      await fetchProject();
      await fetchDriveFiles();
    } catch (err) {
      console.error("Ensure drive folder failed", err);
      setError(t("projectDetails.ensureDriveFolderFailed"));
    }
  };

  const fetchDriveFiles = async (
    { pageSize = 50, pageToken = null, folderId = null } = {},
    projectId = null
  ) => {
    // Accept an optional projectId because callers (like fetchProject) may want to
    // fetch Drive files immediately after receiving project data (before the
    // `project` state has been updated).
    const targetProjectId = projectId || project?._id;
    if (!targetProjectId) return;
    
    const targetFolderId = folderId !== undefined ? folderId : currentFolderId;
    
    setDriveLoading(true);
    setError(null);
    try {
      const params = { pageSize };
      if (pageToken) params.pageToken = pageToken;
      if (targetFolderId) params.folderId = targetFolderId;
      
      const res = await axios.get(`/projects/${targetProjectId}/drive/files`, {
        params,
      });
      
      const files = res.data.files || [];
      
      // Separate folders and files, then sort folders first
      const folders = files.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
      const regularFiles = files.filter(f => f.mimeType !== 'application/vnd.google-apps.folder');
      
      setDriveFiles([...folders, ...regularFiles]);
      setDrivePageToken(res.data.nextPageToken || null);
    } catch (err) {
      console.error("Failed to list drive files", err);
      setError("Failed to list drive files");
    } finally {
      setDriveLoading(false);
    }
  };

  // Folder navigation functions
  const navigateToFolder = (folder) => {
    setFolderStack([...folderStack, { id: currentFolderId || project.driveFolderId, name: folder.name }]);
    setCurrentFolderId(folder.id);
    fetchDriveFiles({ folderId: folder.id });
  };

  const navigateBack = () => {
    if (folderStack.length === 0) return;
    const previous = folderStack[folderStack.length - 1];
    setFolderStack(folderStack.slice(0, -1));
    const newFolderId = previous.id === project.driveFolderId ? null : previous.id;
    setCurrentFolderId(newFolderId);
    fetchDriveFiles({ folderId: newFolderId });
  };

  const navigateToRoot = () => {
    setFolderStack([]);
    setCurrentFolderId(null);
    fetchDriveFiles({ folderId: null });
  };

  const createDriveFolder = async () => {
    if (!project) return;
    const folderName = prompt(t("projectDetails.enterFolderName"));
    if (!folderName) return;
    
    setCreatingFolder(true);
    setError(null);
    try {
      const parentFolderId = currentFolderId || project.driveFolderId;
      await axios.post(`/projects/${project._id}/drive/folders`, {
        name: folderName,
        parentFolderId
      });
      // Stay in the current folder after creating a subfolder
      await fetchDriveFiles({ folderId: currentFolderId });
    } catch (err) {
      console.error('Failed to create folder:', err);
      setError('Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const renameDriveFolder = async (folderId) => {
    if (!canEdit) return;
    const folder = driveFiles.find((f) => f.id === folderId);
    const defaultName = folder?.name || "";
    const newName = prompt(t("projectDetails.renameFolderPrompt"), defaultName);
    if (!newName) return;
    
    try {
      await axios.patch(`/projects/${project._id}/drive/folders/${folderId}`, {
        name: newName,
      });
      await fetchDriveFiles();
    } catch (err) {
      console.error("Rename folder failed", err);
      setError(t("projectDetails.renameFolderFailed"));
    }
  };

  const deleteDriveFolder = async (folderId) => {
    if (!canEdit) return;
    if (!confirm("Delete folder from Google Drive? This will delete all contents.")) return;
    
    try {
      await axios.delete(`/projects/${project._id}/drive/folders/${folderId}`);
      // Stay in the current folder after deletion
      await fetchDriveFiles({ folderId: currentFolderId });
    } catch (err) {
      console.error("Delete folder failed", err);
      setError(t("projectDetails.failedToDeleteFolder"));
    }
  };

  const uploadDriveFile = async (e) => {
    if (!project) return;
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setDriveUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const parentFolderId = currentFolderId || project.driveFolderId;
      fd.append("parentFolderId", parentFolderId);
      
      await axios.post(`/projects/${project._id}/drive/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchDriveFiles();
    } catch (err) {
      console.error("Drive upload failed", err);
      setError("Drive upload failed");
    } finally {
      setDriveUploading(false);
      e.target.value = "";
    }
  };

  // Helper to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = parseInt(bytes, 10);
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const downloadDriveFile = async (fileId) => {
    if (!project || !fileId) return;
    setLoadingDriveFileId(fileId);
    try {
      // Request the proxied download endpoint and ask axios to return a blob.
      const res = await axios.get(
        `/projects/${project._id}/drive/files/${fileId}/download`,
        { responseType: "blob" }
      );
      const blob = res.data;
      // Try to derive filename from Content-Disposition header if present
      let filename = "drive-file";
      const cd =
        res.headers &&
        (res.headers["content-disposition"] ||
          res.headers["Content-Disposition"]);
      if (cd) {
        const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)/i);
        if (match && match[1]) {
          try {
            // Some servers send percent-encoded filenames (RFC5987). decodeURIComponent
            // can throw on malformed encodings, so guard it and fallback to raw value.
            filename = decodeURIComponent(match[1]);
          } catch (e) {
            console.warn(
              "Failed to decode filename from Content-Disposition:",
              e
            );
            filename = match[1];
          }
        }
      } else {
        // fallback: try to find file name from the driveFiles list if available
        const f = driveFiles.find((d) => d.id === fileId);
        if (f && f.name) filename = f.name;
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download drive file failed", err);
      setError(t("projectDetails.downloadDriveFileFailed"));
    } finally {
      setLoadingDriveFileId(null);
    }
  };

  const deleteDriveFile = async (fileId) => {
    if (!canEdit) return;
    if (!confirm("Delete file from Google Drive?")) return;

    // Try to find the file object so we can pass its current name to the server.
    const fileObj = driveFiles.find((d) => d.id === fileId);
    const fileName = fileObj?.name;

    try {
      // Send optional name as query param; server will attempt name-based lookup if id delete returns 404.
      await axios.delete(`/projects/${project._id}/drive/files/${fileId}`, {
        params: { name: fileName },
      });
      // Refresh listing after successful delete
      await fetchDriveFiles();
    } catch (err) {
      console.error("Delete drive file failed", err);
      const status = err?.response?.status;
      if (status === 404) {
        // File already removed in Drive (or name-based lookup couldn't find it) — refresh list and show a friendly message
        await fetchDriveFiles();
        setError(t("projectDetails.driveFileNotFound"));
      } else {
        setError(t("projectDetails.failedToDeleteFile"));
      }
    }
  };

  const renameDriveFile = async (fileId) => {
    if (!canEdit) return;
    // Prefill prompt with current filename (if available)
    const file = driveFiles.find((d) => d.id === fileId);
    const defaultName = file?.name || "";
    const newName = prompt("New file name", defaultName);
    if (!newName) return;
    try {
      await axios.patch(`/projects/${project._id}/drive/files/${fileId}`, {
        name: newName,
      });
      await fetchDriveFiles();
    } catch (err) {
      console.error("Rename failed", err);
      setError(t("projectDetails.renameFailed"));
    }
  };

  // Status badge helper (copied from ProjectsListView for consistent styling)
  const getStatusBadge = (proj) => {
    const status = proj && proj.status;

    if (status === "archived" || proj?.isDeleted) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm bg-amber-700 text-white">
          <ProjectStatusSymbol project={proj} className="h-4 w-4 flex-shrink-0" />
          {t("projects.statusArchived")}
        </span>
      );
    }

    if (status === "done") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm bg-green-700 text-white">
          <ProjectStatusSymbol project={proj} className="h-4 w-4 flex-shrink-0" />
          {t("projects.statusDone")}
        </span>
      );
    }

    if (status === "upcoming") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm bg-gray-200 text-gray-800">
          <ProjectStatusSymbol project={proj} className="h-4 w-4 flex-shrink-0" />
          {t("projects.statusUpcoming")}
        </span>
      );
    }

    if (status === "in_progress") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
          <ProjectStatusSymbol project={proj} className="h-4 w-4 flex-shrink-0" />
          {t("projects.statusInProgress")}
        </span>
      );
    }

    // Fallback: compute from dates if backend status not present
    const now = new Date();
    const start = proj?.startDate ? new Date(proj.startDate) : null;
    const end = proj?.endDate ? new Date(proj.endDate) : null;

    if (end && end < now) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm bg-green-700 text-white">
          <ProjectStatusSymbol project={proj} className="h-4 w-4 flex-shrink-0" />
          {t("projects.statusDone")}
        </span>
      );
    }

    if (start && start > now) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm bg-gray-200 text-gray-800">
          <ProjectStatusSymbol project={proj} className="h-4 w-4 flex-shrink-0" />
          {t("projects.statusUpcoming")}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
        <ProjectStatusSymbol project={proj} className="h-4 w-4 flex-shrink-0" />
        {t("projects.statusInProgress")}
      </span>
    );
  };

  if (loading) {
    return (
      <PageWrapper>
        <p>{t("projectDetails.loadingState")}</p>
      </PageWrapper>
    );
  }

  if (!loading && error && !project && !isNew) {
    return (
      <PageWrapper>
        <p className="text-red-600">{error}</p>
      </PageWrapper>
    );
  }

  const pageTitle = isNew
    ? t("projectDetails.createProject")
    : project?.name || t("projectDetails.title");

  const breadcrumbItems = [{ label: t("projects.title"), to: "/projects" }];
  if (isNew) {
    breadcrumbItems.push({ label: t("projectDetails.createProject") });
  } else if (isEditPath && project) {
    breadcrumbItems.push({ label: project.name, to: `/projects/${id}` });
    breadcrumbItems.push({ label: t("projects.editProject") });
  } else if (project) {
    breadcrumbItems.push({ label: project.name });
  }

  return (
    <>
      <PageHeader
        title={pageTitle}
        breadcrumb={<AdminBreadcrumb items={breadcrumbItems} />}
        actions={
          !isNew && !isEditPath && canEdit ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/projects/${id}/edit`)}
              >
                {t("projects.editProject")}
              </Button>
              <Button onClick={onDeleteProject} variant="danger" size="sm">
                {t("projectDetails.delete")}
              </Button>
            </div>
          ) : null
        }
      />
      <PageWrapper>
        {/* Project form (create/edit) */}
        {(isNew || isEditPath) && canEdit ? (
          <form onSubmit={onSave} className="w-full max-w-xl space-y-4">
            <div>
              <label className="block">Name</label>
              <input
                className="w-full px-3 py-2 border rounded"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block">{t("projectDetails.address")}</label>
              <AddressAutocomplete
                id="address"
                name="address"
                value={form.address}
                onChange={(address, zipCode, city) => {
                  setForm({
                    ...form,
                    address: address,
                    // Only update zipCode and city if they are provided (from autocomplete selection)
                    zipCode: zipCode !== undefined ? zipCode : form.zipCode,
                    city: city !== undefined ? city : form.city,
                  });
                }}
                className="w-full px-3 py-2 border rounded"
                required
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block">{t("projectDetails.zip")}</label>
                <input
                  className="w-full px-3 py-2 border rounded"
                  value={form.zipCode}
                  onChange={(e) =>
                    setForm({ ...form, zipCode: e.target.value })
                  }
                />
              </div>
              <div className="flex-1">
                <label className="block">City</label>
                <input
                  className="w-full px-3 py-2 border rounded"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block">{t("projectDetails.startDate")}</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border rounded"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                />
              </div>
              <div className="flex-1">
                <label className="block">End date</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border rounded"
                  value={form.endDate}
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <label className="block">{t("projectDetails.status")}</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              >
                <option value="">{t("projectDetails.statusNotSet")}</option>
                <option value="in_progress">{t("projects.statusInProgress")}</option>
                <option value="upcoming">{t("projects.statusUpcoming")}</option>
                <option value="done">{t("projects.statusDone")}</option>
                <option value="archived">{t("projects.statusArchived")}</option>
              </select>
            </div>

            <div>
              <label className="block">{t("projectDetails.description")}</label>
              <textarea
                className="w-full px-3 py-2 border rounded"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>

            {/* Billing Configuration Section */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-3">{t("projectDetails.billingConfiguration")}</h3>
              
              {/* Billing Model */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Model
                </label>
                <select
                  value={form.billingModel}
                  onChange={(e) => setForm({ ...form, billingModel: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">{t("projectDetails.billingModelNotSet")}</option>
                  <option value="hourly">{t("report.billingHourly")}</option>
                  <option value="fixed_price">{t("report.billingFixedPrice")}</option>
                </select>
              </div>

              {/* Conditional Hourly Rate */}
              {form.billingModel === "hourly" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("projectDetails.defaultHourlyRate")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.hourlyRate}
                    onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })}
                    placeholder={t("projectDetails.placeholderRate")}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              )}

              {/* Conditional Fixed Price */}
              {form.billingModel === "fixed_price" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("projectDetails.totalBudgetFixedPrice")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.fixedPrice}
                    onChange={(e) => setForm({ ...form, fixedPrice: e.target.value })}
                    placeholder={t("projectDetails.placeholderFixedPrice")}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              )}

              {/* Worker Hourly Rates */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("projectDetails.workerHourlyRates")}
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  {t("projectDetails.workerRatesHint")}
                </p>

                {/* Add Worker Rate UI */}
                <div className="flex gap-2 mb-3">
                  <select
                    value={selectedWorker}
                    onChange={(e) => setSelectedWorker(e.target.value)}
                    className="flex-1 border rounded px-3 py-2 text-sm"
                  >
                    <option value="">{t("projectDetails.selectWorker")}</option>
                    {users
                      .filter((u) => !workerRates.some((wr) => wr.workerId === u._id))
                      .map((u) => (
                        <option key={u._id} value={u._id}>
                          {u.firstname} {u.lastname}
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={selectedWorkerRate}
                    onChange={(e) => setSelectedWorkerRate(e.target.value)}
                    placeholder="Rate"
                    className="w-24 border rounded px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleAddWorkerRate}
                    className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    {t("projectDetails.add")}
                  </button>
                </div>

                {/* Worker Rates List */}
                {workerRates.length > 0 && (
                  <div className="space-y-2">
                    {workerRates.map((wr) => (
                      <div
                        key={wr.workerId}
                        className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm"
                      >
                        <span>
                          {getWorkerName(wr.workerId)}: {wr.hourlyRate} kr/hr
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveWorkerRate(wr.workerId)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Task Configuration Section - Only show for existing projects */}
            {!isNew && id && (
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3">{t("projectDetails.taskConfiguration")}</h3>
                
                {/* Toggle for showing tasks in time report */}
                <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={form.showTasksInTimeReport}
                        onChange={(e) => setForm({ ...form, showTasksInTimeReport: e.target.checked })}
                        className="sr-only"
                      />
                      <div className={`w-9 h-5 rounded-full transition-colors ${
                        form.showTasksInTimeReport ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                          form.showTasksInTimeReport ? 'translate-x-4.5 ml-0.5' : 'translate-x-0.5'
                        }`} style={{ marginLeft: form.showTasksInTimeReport ? '18px' : '2px', marginTop: '3px' }} />
                      </div>
                    </div>
                    <span className="ml-3 text-sm text-gray-700">
                      {t("projectDetails.enableTaskSelection")}
                    </span>
                  </label>
                </div>

                {/* Task Management - Compact Mode */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <ProjectTasksSection 
                    projectId={id} 
                    readOnly={false}
                    compact={true}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Button type="submit" variant="solid" size="md" disabled={saving}>
                {saving ? t("projectDetails.saving") : t("projectDetails.save")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={() => navigate("/projects")}
              >
                {t("projectDetails.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          // Read-only view with hero section
          <div className="space-y-6">
            {/* Hero Section */}
            {project && (
              <>
                <div className="bg-white rounded-lg overflow-hidden shadow-md">
                  {/* Hero Image Section */}
                  <div className="relative h-64 md:h-80 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                    {project.stats?.latestPhotoUrl ? (
                      <img
                        src={getCoverImageUrl(project.stats.latestPhotoUrl)}
                        alt={project.name}
                        className="w-full h-full object-cover"
                        loading="eager"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-gray-400 text-6xl md:text-8xl font-bold opacity-30">
                          {(project.name || "P").charAt(0).toUpperCase()}
                        </div>
                      </div>
                    )}
                    {/* Overlay gradient for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    
                    {/* Content overlay */}
                    <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-8">
                      <div className="flex justify-end items-start">
                        <div className="z-10">{getStatusBadge(project)}</div>
                      </div>

                      {(project.address || project.zipCode || project.city) && (
                        <div className="text-white">
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              `${project.address || ""} ${project.zipCode || ""} ${
                                project.city || ""
                              }`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 text-white/90 hover:text-white transition-colors drop-shadow-md"
                          >
                            <FaMapMarkerAlt className="w-5 h-5 flex-shrink-0" />
                            <span className="text-lg md:text-xl">
                              {project.address && <span>{project.address}, </span>}
                              {project.zipCode && <span>{project.zipCode} </span>}
                              {project.city}
                            </span>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description - part of hero section, below image */}
                  {(displayText(project, "description") || project.description) && (
                    <div className="px-6 md:px-8 pt-4 pb-4">
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap italic">
                        {displayText(project, "description") || project.description}
                      </p>
                    </div>
                  )}
                </div>

                {/* Statistics Display */}
                {project.stats && (
                  <Card>
                    <ProjectStats stats={project.stats} variant="detailed" />
                  </Card>
                )}

                {/* Economy Section - Admin Only (lazy-loaded via IntersectionObserver) */}
                {user?.isAdmin && (
                  <Card ref={economySectionRef}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      <div className="flex items-center gap-2">
                        <FaMoneyBillWave className="w-5 h-5 text-green-600" />
                        <h2 className="text-xl font-bold">{t("projectDetails.economy")}</h2>
                        <div className="flex rounded border border-gray-300 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setEconomyView("per_worker")}
                            className={`px-3 py-1.5 text-sm font-medium ${economyView === "per_worker" ? "bg-gray-200 text-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                          >
                            {t("projectDetails.perWorker")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEconomyView("per_task")}
                            className={`px-3 py-1.5 text-sm font-medium ${economyView === "per_task" ? "bg-gray-200 text-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                          >
                            {t("projectDetails.perTask")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEconomyView("customer")}
                            className={`px-3 py-1.5 text-sm font-medium ${economyView === "customer" ? "bg-gray-200 text-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                          >
                            {t("projectDetails.customer")}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(economyView === "per_worker" || economyView === "customer") && (
                          <DateRangePicker
                            value={economyDateRange}
                            onChange={(range) => {
                              setEconomyDateRange(range);
                              if (!(range?.from && range?.to)) return;
                              if (economyView === "per_worker") fetchEconomy(range);
                              else fetchEconomyByCustomer(range);
                            }}
                            placeholder={t("projectDetails.selectRange")}
                            presets={economyPresets}
                          />
                        )}
                        {(economyView === "per_worker" || economyView === "per_task" || economyView === "customer") && (
                          <button
                            type="button"
                            className="flex items-center justify-start rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
                            onClick={() => {
                              if (economyView === "per_worker") {
                                openEconomyExportModal();
                              } else {
                                handleExportEconomyCSV();
                              }
                            }}
                            disabled={
                              economyView === "per_worker"
                                ? economyLoading || !(economy?.workers?.length > 0)
                                : economyView === "per_task"
                                  ? economyByTaskLoading || !(economyByTask?.tasks?.length > 0)
                                  : economyByCustomerLoading || !(economyByCustomer?.tasks?.length > 0)
                            }
                          >
                            <FaDownload className="mr-2 text-gray-700" />
                            {t("timeReports.exportToCsv")}
                          </button>
                        )}
                        {(economyView === "per_task" || economyView === "customer") && (
                          <button
                            type="button"
                            className="flex items-center justify-start rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-colors disabled:opacity-50"
                            onClick={openAddExpectedCostModal}
                            disabled={!projectTaskBundle?.tasks?.length}
                            title={!projectTaskBundle?.tasks?.length ? t("projectDetails.expectedCostsNoTasks") : undefined}
                          >
                            <FaPlus className="mr-2" />
                            {t("projectDetails.expectedCostsAdd")}
                          </button>
                        )}
                        {economyExportError && (
                          <span className="text-sm text-red-500 whitespace-nowrap">{economyExportError}</span>
                        )}
                      </div>
                    </div>

                    {economyView === "per_worker" && (
                      <>
                        {economyLoading ? (
                          <div className="text-center py-8 text-gray-500">{t("projectDetails.loadingEconomyData")}</div>
                        ) : economy && economy.workers && economy.workers.length > 0 ? (
                          <div className="space-y-4">
                            {economy.allTime && (
                              <p className="text-xs text-gray-500">
                                {t("projectDetails.selectedRangeSinceStart")}{economy.allTime.firstReportDate ? ` (${t("projectDetails.fromDate", { date: dayjs(economy.allTime.firstReportDate).format('D MMM YYYY') })})` : ''}
                              </p>
                            )}
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b">
                                  <tr>
                                    <th className="text-left p-3 font-semibold">{t("projectDetails.worker")}</th>
                                    <th className="text-right p-3 font-semibold">{t("projectDetails.hours")}</th>
                                    <th className="text-right p-3 font-semibold">{t("projectDetails.cost")}</th>
                                    <th className="text-right p-3 font-semibold">{t("projectDetails.billed")}</th>
                                    <th className="text-right p-3 font-semibold">{t("projectDetails.margin")}</th>
                                    <th className="text-right p-3 font-semibold">{t("projectDetails.marginPercent")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {economy.workers.map((worker) => {
                                    const workerIdStr = worker.workerId?.toString?.() ?? worker.workerId;
                                    const allTimeWorker = economy.allTime?.workers?.find((w) => (w.workerId?.toString?.() ?? w.workerId) === workerIdStr);
                                    const stacked = (primary, secondary, primaryClassName = '') => (
                                      <div className="flex flex-col items-end">
                                        <span className={primaryClassName}>{primary}</span>
                                        {secondary != null && economy.allTime && (
                                          <span className="text-xs text-gray-500">{secondary}</span>
                                        )}
                                      </div>
                                    );
                                    return (
                                      <tr key={workerIdStr} className="border-b hover:bg-gray-50">
                                        <td className="p-3 font-medium">{worker.workerName}</td>
                                        <td className="p-3 text-right">
                                          {stacked(
                                            worker.totalHours,
                                            allTimeWorker?.totalHours != null ? `${allTimeWorker.totalHours} ${t("projectDetails.total")}` : null
                                          )}
                                        </td>
                                        <td className="p-3 text-right">
                                          {stacked(
                                            worker.companyCost != null ? `${Math.round(worker.companyCost).toLocaleString('sv-SE')} kr` : 'N/A',
                                            allTimeWorker?.companyCost != null ? `${Math.round(allTimeWorker.companyCost).toLocaleString('sv-SE')} kr ${t("projectDetails.total")}` : null
                                          )}
                                        </td>
                                        <td className="p-3 text-right">
                                          {stacked(
                                            worker.billed != null ? `${Math.round(worker.billed).toLocaleString('sv-SE')} kr` : 'N/A',
                                            allTimeWorker?.billed != null ? `${Math.round(allTimeWorker.billed).toLocaleString('sv-SE')} kr ${t("projectDetails.total")}` : null
                                          )}
                                        </td>
                                        <td className={`p-3 text-right font-medium ${
                                          worker.margin != null ? worker.margin >= 0 ? 'text-green-600' : 'text-red-600' : ''
                                        }`}>
                                          {stacked(
                                            worker.margin != null ? `${Math.round(worker.margin).toLocaleString('sv-SE')} kr` : 'N/A',
                                            allTimeWorker?.margin != null ? `${Math.round(allTimeWorker.margin).toLocaleString('sv-SE')} kr ${t("projectDetails.total")}` : null,
                                            worker.margin != null ? worker.margin >= 0 ? 'text-green-600' : 'text-red-600' : ''
                                          )}
                                        </td>
                                        <td className={`p-3 text-right font-medium ${
                                          worker.marginPercent != null ? worker.marginPercent >= 0 ? 'text-green-600' : 'text-red-600' : ''
                                        }`}>
                                          {stacked(
                                            worker.marginPercent != null ? `${worker.marginPercent.toFixed(1)}%` : 'N/A',
                                            allTimeWorker?.marginPercent != null ? `${allTimeWorker.marginPercent.toFixed(1)}% ${t("projectDetails.total")}` : null,
                                            worker.marginPercent != null ? worker.marginPercent >= 0 ? 'text-green-600' : 'text-red-600' : ''
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot className="bg-gray-100 font-bold">
                                  <tr>
                                    <td className="p-3">{t("projectDetails.totalLabel")}</td>
                                    <td className="p-3 text-right">
                                      <div className="flex flex-col items-end">
                                        <span>{economy.workers.reduce((sum, w) => sum + (w.totalHours || 0), 0)}</span>
                                        {economy.allTime?.totals && (
                                          <span className="text-xs text-gray-500">
                                            {economy.allTime.workers?.reduce((sum, w) => sum + (w.totalHours || 0), 0) ?? '—'} {t("projectDetails.total")}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="p-3 text-right">
                                      <div className="flex flex-col items-end">
                                        <span>{Math.round(economy.totals.totalCompanyCost).toLocaleString('sv-SE')} kr</span>
                                        {economy.allTime?.totals && (
                                          <span className="text-xs text-gray-500">
                                            {Math.round(economy.allTime.totals.totalCompanyCost).toLocaleString('sv-SE')} kr {t("projectDetails.total")}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="p-3 text-right">
                                      <div className="flex flex-col items-end">
                                        <span>{Math.round(economy.totals.totalBilled).toLocaleString('sv-SE')} kr</span>
                                        {economy.allTime?.totals && (
                                          <span className="text-xs text-gray-500">
                                            {Math.round(economy.allTime.totals.totalBilled).toLocaleString('sv-SE')} kr {t("projectDetails.total")}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className={`p-3 text-right ${
                                      economy.totals.totalMargin >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                      <div className="flex flex-col items-end">
                                        <span>{Math.round(economy.totals.totalMargin).toLocaleString('sv-SE')} kr</span>
                                        {economy.allTime?.totals && (
                                          <span className="text-xs text-gray-500">
                                            {Math.round(economy.allTime.totals.totalMargin).toLocaleString('sv-SE')} kr {t("projectDetails.total")}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className={`p-3 text-right ${
                                      economy.totals.totalMarginPercent >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                      <div className="flex flex-col items-end">
                                        <span>{economy.totals.totalMarginPercent.toFixed(1)}%</span>
                                        {economy.allTime?.totals && (
                                          <span className="text-xs text-gray-500">
                                            {economy.allTime.totals.totalMarginPercent.toFixed(1)}% {t("projectDetails.total")}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        ) : !economyLoading ? (
                          <p className="text-sm text-gray-600 text-center py-8">{t("projectDetails.noEconomyDataAvailable")}</p>
                        ) : null}
                      </>
                    )}

                    {economyView === "per_task" && (
                      <>
                        {economyByTaskLoading ? (
                          <div className="text-center py-8 text-gray-500">{t("projectDetails.loadingEconomyByTask")}</div>
                        ) : economyByTask?.tasks?.length > 0 ? (
                          (() => {
                            const formatKr = (v) => (v != null && Number(v) > 0 ? `${Math.round(Number(v)).toLocaleString("sv-SE")} kr` : "—");
                            const marginTextClass = (margin) =>
                              margin == null ? "" : margin >= 0 ? "text-green-600" : "text-red-600";
                            const formatMarginCell = (margin) => (margin != null ? formatEconomyMarginKr(margin) : "—");
                            const formatMarginPctCell = (marginPct) =>
                              marginPct != null ? `${marginPct.toFixed(1)}%` : "—";
                            const renderRow = (row, { isSubtask, parentTask }) => {
                              const margins = isSubtask ? null : perTaskMargins(parentTask);
                              const rowMargin = isSubtask ? perTaskMarginForRow(row) : null;
                              const priceParts = isSubtask ? null : perTaskPriceParts(parentTask);
                              const billingLabel = row.includedInParent
                                ? t("projectDetails.includedInParentTask")
                                : row.billingModel
                                  ? String(row.billingModel).replace("_", " ")
                                  : "—";
                              const taskHeadingBase = () => {
                                const taskPart =
                                  displayText(parentTask, "title") || parentTask.title || "—";
                                return isSubtask
                                  ? `${taskPart} → ${displayText(row, "title") || row.title || "—"}`
                                  : taskPart;
                              };
                              const rolledSubtaskIdsForRow = isSubtask
                                ? undefined
                                : (parentTask.subtasks || [])
                                    .map((s) => s._id);
                              const taskRollups = !isSubtask ? getEconomyTaskRollups(parentTask) : null;
                              const buildHoursCell = (hours, rolledSubtaskIds, summed = false) => {
                                if (hours == null || hours <= 0) return "—";
                                return (
                                  <button
                                    type="button"
                                    className={`${
                                      summed
                                        ? "text-gray-600 italic underline decoration-gray-400 decoration-1 underline-offset-2"
                                        : "text-gray-900 underline decoration-gray-900 underline-offset-2"
                                    } hover:opacity-80 font-medium cursor-pointer tabular-nums`}
                                    onClick={() => {
                                      void openEconomyTaskHoursModal({
                                        taskId: parentTask._id,
                                        subtaskId: isSubtask ? row._id : undefined,
                                        rolledSubtaskIds,
                                        heading: taskHeadingBase(),
                                      });
                                    }}
                                  >
                                    {summed ? `(${hours})` : hours}
                                  </button>
                                );
                              };
                              const atHours = isSubtask
                                ? row.reportedHours
                                : taskRollups.atTaskLevel.reportedHours;
                              const totalHours = isSubtask ? null : taskRollups.includingSubtasks.reportedHours;
                              const hoursCell =
                                !isSubtask && taskRollups.hasSubtasks ? (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={buildHoursCell(atHours, [])}
                                    total={buildHoursCell(totalHours, rolledSubtaskIdsForRow, true)}
                                  />
                                ) : (
                                  buildHoursCell(row.reportedHours, rolledSubtaskIdsForRow)
                                );
                              const atInvoiceMaterials = isSubtask
                                ? Number(row.materialsFromInvoices ?? row.materialsAndSubs ?? 0)
                                : Number(
                                    taskRollups.atTaskLevel.materialsFromInvoices ??
                                      taskRollups.atTaskLevel.materialsAndSubs ??
                                      0
                                  );
                              const totalInvoiceMaterials = isSubtask
                                ? null
                                : Number(
                                    taskRollups.includingSubtasks.materialsFromInvoices ??
                                      taskRollups.includingSubtasks.materialsAndSubs ??
                                      0
                                  );
                              const atExpectedMaterials = isSubtask
                                ? Number(row.materialsFromAccruals ?? 0)
                                : Number(taskRollups.atTaskLevel.materialsFromAccruals ?? 0);
                              const totalExpectedMaterials = isSubtask
                                ? null
                                : Number(taskRollups.includingSubtasks.materialsFromAccruals ?? 0);
                              const buildMaterialsCell = (amount, rolledSubtaskIds, summed = false) => {
                                if (amount <= 0) return formatKr(amount);
                                return (
                                  <button
                                    type="button"
                                    className={`${
                                      summed
                                        ? "text-gray-600 italic underline decoration-gray-400 decoration-1 underline-offset-2"
                                        : "text-gray-900 underline decoration-gray-900 underline-offset-2"
                                    } hover:opacity-80 font-medium cursor-pointer tabular-nums`}
                                    onClick={() => {
                                      void openEconomyTaskMaterialsModal({
                                        taskId: parentTask._id,
                                        subtaskId: isSubtask ? row._id : undefined,
                                        rolledSubtaskIds,
                                        heading: taskHeadingBase(),
                                        customerView: false,
                                      });
                                    }}
                                  >
                                    {summed ? `(${formatKr(amount)})` : formatKr(amount)}
                                  </button>
                                );
                              };
                              const buildExpectedCostsCell = (amount, rolledSubtaskIds, summed = false) => {
                                if (amount <= 0) return formatKr(amount);
                                return (
                                  <button
                                    type="button"
                                    className={`${
                                      summed
                                        ? "text-gray-600 italic underline decoration-gray-400 decoration-1 underline-offset-2"
                                        : "text-gray-900 underline decoration-gray-900 underline-offset-2"
                                    } hover:opacity-80 font-medium cursor-pointer tabular-nums`}
                                    onClick={() => {
                                      void openEconomyTaskExpectedCostsModal({
                                        taskId: parentTask._id,
                                        subtaskId: isSubtask ? row._id : undefined,
                                        rolledSubtaskIds,
                                        heading: taskHeadingBase(),
                                        customerView: false,
                                      });
                                    }}
                                  >
                                    {summed ? `(${formatKr(amount)})` : formatKr(amount)}
                                  </button>
                                );
                              };
                              const materialsCell =
                                !isSubtask && taskRollups.hasSubtasks ? (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={buildMaterialsCell(atInvoiceMaterials, [])}
                                    total={buildMaterialsCell(totalInvoiceMaterials, rolledSubtaskIdsForRow, true)}
                                  />
                                ) : (
                                  buildMaterialsCell(
                                    Number(row.materialsFromInvoices ?? row.materialsAndSubs ?? 0),
                                    rolledSubtaskIdsForRow
                                  )
                                );
                              const expectedCostsCell =
                                !isSubtask && taskRollups.hasSubtasks ? (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={buildExpectedCostsCell(atExpectedMaterials, [])}
                                    total={buildExpectedCostsCell(totalExpectedMaterials, rolledSubtaskIdsForRow, true)}
                                  />
                                ) : (
                                  buildExpectedCostsCell(
                                    Number(row.materialsFromAccruals ?? 0),
                                    rolledSubtaskIdsForRow
                                  )
                                );
                              const atLabourCost = isSubtask
                                ? row.labourCost
                                : taskRollups.atTaskLevel.labourCost;
                              const totalLabourCost = isSubtask ? null : taskRollups.includingSubtasks.labourCost;
                              const labourCostCell =
                                !isSubtask && taskRollups.hasSubtasks ? (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={formatKr(atLabourCost)}
                                    total={`(${formatKr(totalLabourCost)})`}
                                  />
                                ) : (
                                  formatKr(row.labourCost)
                                );
                              return (
                                <>
                                  <td className="p-3 text-right">{hoursCell}</td>
                                  <td className="p-3 text-right">{labourCostCell}</td>
                                  <td className="p-3 text-right">{materialsCell}</td>
                                  <td className="p-3 text-right">{expectedCostsCell}</td>
                                  <td className="p-3 text-right">{billingLabel}</td>
                                  <td className="p-3 text-right">{row.estimatedHours != null ? row.estimatedHours : "—"}</td>
                                  <td className="p-3 text-right">
                                    {priceParts?.hasSubtasks ? (
                                      <EconomyTaskMetricStack
                                        hasSubtasks
                                        primary={formatKr(priceParts.own)}
                                        total={`(${formatKr(priceParts.total)})`}
                                      />
                                    ) : (
                                      formatKr(row.estimatedCost)
                                    )}
                                  </td>
                                  <td className="p-3 text-right">
                                    {margins?.hasSubtasks ? (
                                      <EconomyTaskMetricStack
                                        hasSubtasks
                                        primary={
                                          <span className={marginTextClass(margins.own.margin)}>
                                            {formatMarginCell(margins.own.margin)}
                                          </span>
                                        }
                                        total={
                                          <span className={marginTextClass(margins.total.margin)}>
                                            ({formatMarginCell(margins.total.margin)})
                                          </span>
                                        }
                                      />
                                    ) : (
                                      <span className={marginTextClass(rowMargin?.margin ?? margins?.own.margin)}>
                                        {formatMarginCell(rowMargin?.margin ?? margins?.own.margin)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-3 text-right">
                                    {margins?.hasSubtasks ? (
                                      <EconomyTaskMetricStack
                                        hasSubtasks
                                        primary={
                                          <span className={marginTextClass(margins.own.marginPct)}>
                                            {formatMarginPctCell(margins.own.marginPct)}
                                          </span>
                                        }
                                        total={
                                          <span className={marginTextClass(margins.total.marginPct)}>
                                            ({formatMarginPctCell(margins.total.marginPct)})
                                          </span>
                                        }
                                      />
                                    ) : (
                                      <span className={marginTextClass(rowMargin?.marginPct ?? margins?.own.marginPct)}>
                                        {formatMarginPctCell(rowMargin?.marginPct ?? margins?.own.marginPct)}
                                      </span>
                                    )}
                                  </td>
                                </>
                              );
                            };
                            const totals = sumDisplayedPerTaskTotals(economyByTask.tasks);
                            const totalMarginPct = totals.price > 0 ? (totals.margin / totals.price) * 100 : null;
                            return (
                              <div className="space-y-4">
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b">
                                      <tr>
                                        <th className="text-left p-3 font-semibold">{t("projectDetails.taskSubtask")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.reportedHours")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.labourCost")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.materialsAndSubs")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.expectedCostsColumn")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.billingModelCol")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.estimatedHours")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.estimatedCost")}</th>
                                        <th
                                          className="text-right p-3 font-semibold"
                                          title={`${t("projectDetails.estimatedCost")} − ${t("projectDetails.labourCost")} − ${t("projectDetails.materialsAndSubs")} − ${t("projectDetails.expectedCostsColumn")}`}
                                        >
                                          {t("projectDetails.margin")}
                                        </th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.marginPercent")}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {economyByTask.tasks.map((task) => (
                                        <React.Fragment key={task._id}>
                                          <tr className="border-b hover:bg-gray-50">
                                            <td className="p-3 font-medium">{displayText(task, "title") || task.title || "—"}</td>
                                            {renderRow(task, { isSubtask: false, parentTask: task })}
                                          </tr>
                                          {(task.subtasks || []).map((sub) => (
                                            <tr key={sub._id} className="border-b hover:bg-gray-50 bg-gray-50/50">
                                              <td className="p-3 pl-6 text-gray-700">{displayText(sub, "title") || sub.title || "—"}</td>
                                              {renderRow(sub, { isSubtask: true, parentTask: task })}
                                            </tr>
                                          ))}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-bold">
                                      <tr>
                                        <td className="p-3">{t("projectDetails.totalLabel")}</td>
                                        <td className="p-3 text-right">{totals.hours > 0 ? totals.hours : "—"}</td>
                                        <td className="p-3 text-right">{formatKr(totals.labour)}</td>
                                        <td className="p-3 text-right">{formatKr(totals.materials)}</td>
                                        <td className="p-3 text-right">{formatKr(totals.expected)}</td>
                                        <td className="p-3 text-right">—</td>
                                        <td className="p-3 text-right">—</td>
                                        <td className="p-3 text-right">
                                          {totals.price > 0 ? formatKr(totals.price) : totals.hasMargin ? "0 kr" : "—"}
                                        </td>
                                        <td className={`p-3 text-right ${totals.hasMargin ? (totals.margin >= 0 ? "text-green-600" : "text-red-600") : ""}`}>
                                          {totals.hasMargin ? formatEconomyMarginKr(totals.margin) : "—"}
                                        </td>
                                        <td className={`p-3 text-right ${totalMarginPct != null ? (totalMarginPct >= 0 ? "text-green-600" : "text-red-600") : ""}`}>
                                          {totalMarginPct != null ? `${totalMarginPct.toFixed(1)}%` : "—"}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            );
                          })()
                        ) : !economyByTaskLoading ? (
                          <p className="text-sm text-gray-600 text-center py-8">{t("projectDetails.noTasksAddInTasksSection")}</p>
                        ) : null}
                      </>
                    )}

                    {economyView === "customer" && (
                      <>
                        {economyByCustomerLoading ? (
                          <div className="text-center py-8 text-gray-500">{t("projectDetails.loadingEconomyByTask")}</div>
                        ) : economyByCustomer?.tasks?.length > 0 ? (
                          (() => {
                            const formatKr = (v) => (v != null && Number(v) > 0 ? `${Math.round(Number(v)).toLocaleString("sv-SE")} kr` : "—");
                            const formatKrOrZero = (v) =>
                              v != null && Number(v) > 0 ? `${Math.round(Number(v)).toLocaleString("sv-SE")} kr` : "0 kr";
                            const addVat = (v) => Number(v ?? 0) * 1.25;
                            const scaleMaterials = (v) => Math.ceil(Number(v ?? 0) * 1.15 * 1.25);
                            const formatMaterialsAndSubs = (v) => {
                              const scaled = scaleMaterials(v);
                              return scaled > 0 ? `${scaled.toLocaleString("sv-SE")} kr` : "0 kr";
                            };
                            const getEstimatedCostInclVat = (v) =>
                              v != null && Number(v) > 0 ? addVat(v) : null;
                            const getActualCost = (row) =>
                              addVat(row.billedAmount) + scaleMaterials(row.materialsAndSubs);
                            const getActualCostIncludingSubtasks = (task) =>
                              getActualCost(task) + (task.subtasks || []).reduce((s, sub) => s + getActualCost(sub), 0);

                            const displayedTotals = sumCustomerEconomyDisplayedTaskTotals(economyByCustomer.tasks);
                            const totals = economyByCustomer.tasks.reduce(
                              (acc, t) => ({
                                estimatedCost:
                                  acc.estimatedCost + Number(t.estimatedCost ?? 0) + (t.subtasks || []).reduce((s, st) => s + Number(st.estimatedCost ?? 0), 0),
                              }),
                              { estimatedCost: 0 }
                            );
                            const totalActualCost = economyByCustomer.tasks.reduce(
                              (sum, t) => sum + getActualCostIncludingSubtasks(t),
                              0
                            );
                            const totalEstimatedCostDisplay = (() => {
                              const estimatedInclVat = getEstimatedCostInclVat(totals.estimatedCost);
                              const main = estimatedInclVat != null ? formatKr(estimatedInclVat) : "—";
                              const actual = formatKrOrZero(totalActualCost);
                              return `${main} / ${actual}`;
                            })();

                            const renderEstimatedCostCell = (row, actualTotalCost, isTaskRow = false, exportCtx) => {
                              const estimatedInclVat = getEstimatedCostInclVat(row.estimatedCost);
                              const main = estimatedInclVat != null ? formatKr(estimatedInclVat) : "—";
                              const actual = formatKrOrZero(actualTotalCost);
                              const exportKey = exportCtx
                                ? `${exportCtx.taskId}${exportCtx.subtaskId ? `-${exportCtx.subtaskId}` : ""}`
                                : null;
                              const isExporting = exportKey && customerTaskCsvExportKey === exportKey;
                              return (
                                <td className={`p-3 text-right ${isTaskRow ? "font-semibold" : ""}`}>
                                  <div className="inline-flex items-center justify-end gap-1.5">
                                    <span className="tabular-nums">{`${main} / ${actual}`}</span>
                                    {exportCtx && (
                                      <button
                                        type="button"
                                        className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                                        onClick={() => {
                                          void exportCustomerTaskEconomyCsv({
                                            taskId: exportCtx.taskId,
                                            subtaskId: exportCtx.subtaskId,
                                            rolledSubtaskIds: exportCtx.rolledSubtaskIds,
                                            heading: exportCtx.heading,
                                            estimatedCostExVat: row.estimatedCost,
                                            actualTotalCostInclVat: actualTotalCost,
                                          });
                                        }}
                                        disabled={isExporting}
                                        aria-label={t("timeReports.exportToCsv")}
                                        title={t("timeReports.exportToCsv")}
                                      >
                                        <FaDownload className="h-3.5 w-3.5" aria-hidden />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              );
                            };

                            const customerTaskHeading = (row, isSubtask, parentTask) => {
                              const taskPart =
                                displayText(parentTask, "title") || parentTask.title || "—";
                              return isSubtask
                                ? `${taskPart} → ${displayText(row, "title") || row.title || "—"}`
                                : taskPart;
                            };

                            const rolledSubtaskIdsForCustomerTask = (parentTask) =>
                              (parentTask.subtasks || [])
                                .map((s) => s._id);

                            const renderCustomerHoursCell = (
                              hours,
                              { row, isSubtask, parentTask, rolledSubtaskIds, summed = false }
                            ) => {
                              if (hours == null || hours <= 0) {
                                return "—";
                              }
                              return (
                                <button
                                  type="button"
                                  className={`${
                                    summed
                                      ? "text-gray-600 italic underline decoration-gray-400 decoration-1 underline-offset-2"
                                      : "text-gray-900 underline decoration-gray-900 underline-offset-2"
                                  } hover:opacity-80 font-medium cursor-pointer tabular-nums`}
                                  onClick={() => {
                                    void openEconomyTaskHoursModal({
                                      taskId: parentTask._id,
                                      subtaskId: isSubtask ? row._id : undefined,
                                      rolledSubtaskIds,
                                      heading: customerTaskHeading(row, isSubtask, parentTask),
                                    });
                                  }}
                                >
                                  {summed ? `(${hours})` : hours}
                                </button>
                              );
                            };

                            const renderCustomerMaterialsCell = (
                              invoiceMaterials,
                              { row, isSubtask, parentTask, rolledSubtaskIds, summed = false, displayKr = null }
                            ) => {
                              const shownKr = displayKr != null ? Number(displayKr) : null;
                              const display =
                                shownKr != null
                                  ? formatCustomerEconomyDisplayedKr(shownKr)
                                  : formatMaterialsAndSubs(invoiceMaterials);
                              if ((shownKr != null ? shownKr : invoiceMaterials) <= 0) {
                                return display;
                              }
                              return (
                                <button
                                  type="button"
                                  className={`${
                                    summed
                                      ? "text-gray-600 italic underline decoration-gray-400 decoration-1 underline-offset-2"
                                      : "text-gray-900 underline decoration-gray-900 underline-offset-2"
                                  } hover:opacity-80 font-medium cursor-pointer tabular-nums`}
                                  onClick={() => {
                                    void openEconomyTaskMaterialsModal({
                                      taskId: parentTask._id,
                                      subtaskId: isSubtask ? row._id : undefined,
                                      rolledSubtaskIds,
                                      heading: customerTaskHeading(row, isSubtask, parentTask),
                                      customerView: true,
                                    });
                                  }}
                                >
                                  {summed ? `(${display})` : display}
                                </button>
                              );
                            };

                            const renderCustomerExpectedCostsCell = (
                              expectedMaterials,
                              { row, isSubtask, parentTask, rolledSubtaskIds, summed = false, displayKr = null }
                            ) => {
                              const shownKr = displayKr != null ? Number(displayKr) : null;
                              const display =
                                shownKr != null
                                  ? formatCustomerEconomyDisplayedKr(shownKr)
                                  : formatMaterialsAndSubs(expectedMaterials);
                              if ((shownKr != null ? shownKr : expectedMaterials) <= 0) {
                                return display === "0 kr" ? "0 kr" : display;
                              }
                              return (
                                <button
                                  type="button"
                                  className={`${
                                    summed
                                      ? "text-gray-600 italic underline decoration-gray-400 decoration-1 underline-offset-2"
                                      : "text-gray-900 underline decoration-gray-900 underline-offset-2"
                                  } hover:opacity-80 font-medium cursor-pointer tabular-nums`}
                                  onClick={() => {
                                    void openEconomyTaskExpectedCostsModal({
                                      taskId: parentTask._id,
                                      subtaskId: isSubtask ? row._id : undefined,
                                      rolledSubtaskIds,
                                      heading: customerTaskHeading(row, isSubtask, parentTask),
                                      customerView: true,
                                    });
                                  }}
                                >
                                  {summed ? `(${display})` : display}
                                </button>
                              );
                            };

                            const renderCustomerTaskMetricCells = (task) => {
                              const rollups = getEconomyTaskRollups(task);
                              const displayed = getCustomerEconomyDisplayedParts(task);
                              const rolledIds = rolledSubtaskIdsForCustomerTask(task);
                              const cellCtx = { row: task, isSubtask: false, parentTask: task };
                              if (!rollups.hasSubtasks) {
                                return {
                                  hours: renderCustomerHoursCell(task.reportedHours, {
                                    ...cellCtx,
                                    rolledSubtaskIds: [],
                                  }),
                                  cost: formatKrOrZero(addVat(task.billedAmount)),
                                  materials: renderCustomerMaterialsCell(
                                    Number(task.materialsFromInvoices ?? task.materialsAndSubs ?? 0),
                                    { ...cellCtx, rolledSubtaskIds: [] }
                                  ),
                                  expected: renderCustomerExpectedCostsCell(
                                    Number(task.materialsFromAccruals ?? 0),
                                    { ...cellCtx, rolledSubtaskIds: [] }
                                  ),
                                };
                              }
                              return {
                                hours: (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={renderCustomerHoursCell(rollups.atTaskLevel.reportedHours, {
                                      ...cellCtx,
                                      rolledSubtaskIds: [],
                                    })}
                                    total={renderCustomerHoursCell(displayed.total.reportedHours, {
                                      ...cellCtx,
                                      rolledSubtaskIds: rolledIds,
                                      summed: true,
                                    })}
                                  />
                                ),
                                cost: (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={formatKrOrZero(addVat(rollups.atTaskLevel.billedAmount))}
                                    total={`(${formatCustomerEconomyDisplayedKr(displayed.total.costInclVat)})`}
                                  />
                                ),
                                materials: (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={renderCustomerMaterialsCell(
                                      Number(
                                        rollups.atTaskLevel.materialsFromInvoices ??
                                          rollups.atTaskLevel.materialsAndSubs ??
                                          0
                                      ),
                                      { ...cellCtx, rolledSubtaskIds: [] }
                                    )}
                                    total={renderCustomerMaterialsCell(0, {
                                      ...cellCtx,
                                      rolledSubtaskIds: rolledIds,
                                      summed: true,
                                      displayKr: displayed.total.materialsInclVat,
                                    })}
                                  />
                                ),
                                expected: (
                                  <EconomyTaskMetricStack
                                    hasSubtasks
                                    primary={renderCustomerExpectedCostsCell(
                                      Number(rollups.atTaskLevel.materialsFromAccruals ?? 0),
                                      { ...cellCtx, rolledSubtaskIds: [] }
                                    )}
                                    total={renderCustomerExpectedCostsCell(0, {
                                      ...cellCtx,
                                      rolledSubtaskIds: rolledIds,
                                      summed: true,
                                      displayKr: displayed.total.expectedInclVat,
                                    })}
                                  />
                                ),
                              };
                            };

                            return (
                              <div className="space-y-4">
                                <CustomerEconomyHelp />
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b">
                                      <tr>
                                        <th className="text-left p-3 font-semibold">{t("projectDetails.taskSubtask")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.reportedHours")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.costInclVat")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.materialsAndSubsInclVat")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.expectedCostsColumnInclVat")}</th>
                                        <th className="text-right p-3 font-semibold">{t("projectDetails.estimatedHours")}</th>
                                        <th className="text-right p-3 font-semibold">
                                          {(() => {
                                            const header = t("projectDetails.estimatedCostInclVat");
                                            const slashIndex = header.indexOf("/");
                                            if (slashIndex === -1) return header;
                                            const firstLine = header.slice(0, slashIndex + 1).trimEnd();
                                            const secondLine = header.slice(slashIndex + 1).trimStart();
                                            return (
                                              <>
                                                {firstLine}
                                                <br />
                                                {secondLine}
                                              </>
                                            );
                                          })()}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {economyByCustomer.tasks.map((task) => {
                                        const taskMetrics = renderCustomerTaskMetricCells(task);
                                        return (
                                        <React.Fragment key={task._id}>
                                          <tr className="border-b hover:bg-gray-50">
                                            <td className="p-3 font-medium">{displayText(task, "title") || task.title || "—"}</td>
                                            <td className="p-3 text-right">{taskMetrics.hours}</td>
                                            <td className="p-3 text-right">{taskMetrics.cost}</td>
                                            <td className="p-3 text-right">{taskMetrics.materials}</td>
                                            <td className="p-3 text-right">{taskMetrics.expected}</td>
                                            <td className="p-3 text-right">{task.estimatedHours != null ? task.estimatedHours : "—"}</td>
                                            {renderEstimatedCostCell(task, getActualCostIncludingSubtasks(task), true, {
                                              taskId: task._id,
                                              rolledSubtaskIds: rolledSubtaskIdsForCustomerTask(task),
                                              heading: displayText(task, "title") || task.title || "—",
                                            })}
                                          </tr>
                                          {(task.subtasks || []).map((sub) => (
                                            <tr key={sub._id} className="border-b hover:bg-gray-50 bg-gray-50/50">
                                              <td className="p-3 pl-6 text-gray-700">{displayText(sub, "title") || sub.title || "—"}</td>
                                              <td className="p-3 text-right">
                                                {renderCustomerHoursCell(sub.reportedHours, {
                                                  row: sub,
                                                  isSubtask: true,
                                                  parentTask: task,
                                                  rolledSubtaskIds: undefined,
                                                })}
                                              </td>
                                              <td className="p-3 text-right">{formatKrOrZero(addVat(sub.billedAmount))}</td>
                                              <td className="p-3 text-right">
                                                {renderCustomerMaterialsCell(
                                                  Number(sub.materialsFromInvoices ?? sub.materialsAndSubs ?? 0),
                                                  {
                                                    row: sub,
                                                    isSubtask: true,
                                                    parentTask: task,
                                                    rolledSubtaskIds: undefined,
                                                  }
                                                )}
                                              </td>
                                              <td className="p-3 text-right">
                                                {renderCustomerExpectedCostsCell(Number(sub.materialsFromAccruals ?? 0), {
                                                  row: sub,
                                                  isSubtask: true,
                                                  parentTask: task,
                                                  rolledSubtaskIds: undefined,
                                                })}
                                              </td>
                                              <td className="p-3 text-right">{sub.estimatedHours != null ? sub.estimatedHours : "—"}</td>
                                              {renderEstimatedCostCell(sub, getActualCost(sub), false, {
                                                taskId: task._id,
                                                subtaskId: sub._id,
                                                heading: customerTaskHeading(sub, true, task),
                                              })}
                                            </tr>
                                          ))}
                                        </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-bold">
                                      <tr>
                                        <td className="p-3">{t("projectDetails.totalLabel")}</td>
                                        <td className="p-3 text-right">{displayedTotals.reportedHours > 0 ? displayedTotals.reportedHours : "—"}</td>
                                        <td className="p-3 text-right">{formatCustomerEconomyDisplayedKr(displayedTotals.costInclVat)}</td>
                                        <td className="p-3 text-right">{formatCustomerEconomyDisplayedKr(displayedTotals.materialsInclVat)}</td>
                                        <td className="p-3 text-right">{formatCustomerEconomyDisplayedKr(displayedTotals.expectedInclVat)}</td>
                                        <td className="p-3 text-right">—</td>
                                        <td className="p-3 text-right">{totalEstimatedCostDisplay}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            );
                          })()
                        ) : !economyByCustomerLoading ? (
                          <p className="text-sm text-gray-600 text-center py-8">{t("projectDetails.noTasksAddInTasksSection")}</p>
                        ) : null}
                      </>
                    )}

                  </Card>
                )}

                {/* Google Drive */}
                <Card>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">{t("projectDetails.googleDrive")}</h2>
                  </div>

                  {!project?.driveFolderId ? (
                    <div className="text-sm text-gray-600">
                      {canEdit ? (
                        <div>
                          <p>
                            {t("projectDetails.noDriveFolderLinked")}
                          </p>
                          <div className="mt-2">
                            <button
                              className="px-3 py-1 bg-brand text-white rounded"
                              onClick={ensureDriveFolder}
                            >
                              {t("projectDetails.linkCreateDriveFolder")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p>{t("projectDetails.driveFolderNotLinkedYet")}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      {/* Breadcrumb Navigation */}
                      {(folderStack.length > 0 || currentFolderId) && (
                        <div className="flex items-center gap-2 mb-3 text-sm text-gray-600 p-2 bg-gray-50 rounded">
                          <button
                            onClick={navigateToRoot}
                            className="hover:text-blue-600 transition-colors"
                            title="Go to root"
                          >
                            <FaHome className="w-4 h-4" />
                          </button>
                          {folderStack.length > 0 && (
                            <>
                              <span>/</span>
                              <button
                                onClick={navigateBack}
                                className="hover:text-blue-600 transition-colors flex items-center gap-1"
                                title={t("projectDetails.goBack")}
                              >
                                <FaArrowLeft className="w-3 h-3" />
                                <span>{t("projectDetails.goBack")}</span>
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      {canEdit && (
                        <div className="flex gap-2 mb-3">
                          <button
                            onClick={createDriveFolder}
                            disabled={creatingFolder}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <FaFolderPlus className="w-4 h-4" />
                            New Folder
                          </button>
                          <label className="cursor-pointer flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors">
                            <FaPlus className="w-4 h-4" />
                            Add File
                            <input
                              type="file"
                              className="hidden"
                              onChange={uploadDriveFile}
                              disabled={driveUploading}
                            />
                          </label>
                        </div>
                      )}

                      {driveLoading ? (
                        <div className="p-3 text-center text-gray-500">{t("projectDetails.driveLoading")}</div>
                      ) : driveFiles && driveFiles.length ? (
                        <ul className="space-y-2">
                          {driveFiles.map((item) => {
                            const isFolder = item.mimeType === 'application/vnd.google-apps.folder';
                            const isLoading = loadingDriveFileId === item.id;
                            
                            return (
                              <li
                                key={item.id}
                                className={`flex items-center justify-between p-2 hover:bg-gray-50 rounded transition-colors ${isLoading ? 'bg-blue-50' : ''}`}
                              >
                                <div 
                                  className={`flex items-center space-x-3 flex-1 ${isFolder || !isLoading ? 'cursor-pointer' : ''} ${isLoading ? 'opacity-70' : ''}`}
                                  onClick={() => {
                                    if (isLoading) return;
                                    if (isFolder) {
                                      navigateToFolder(item);
                                    } else {
                                      downloadDriveFile(item.id);
                                    }
                                  }}
                                >
                                  {isFolder ? (
                                    <FaFolder className="w-8 h-8 text-yellow-500 flex-shrink-0" />
                                  ) : isLoading ? (
                                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                  ) : (
                                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded text-xs flex-shrink-0">
                                      {item.mimeType === "application/pdf" ? "PDF" : "FILE"}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className={`font-medium break-words ${!isFolder ? 'hover:text-blue-600 hover:underline' : ''}`}>
                                      {item.name}
                                      {isLoading && <span className="ml-2 text-xs text-blue-600 font-normal">Loading...</span>}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {item.modifiedTime
                                        ? new Date(item.modifiedTime).toLocaleDateString()
                                        : ""}
                                      {!isFolder && item.size && (
                                        <span className="ml-2">{formatFileSize(item.size)}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center space-x-1 flex-shrink-0">
                                  {!isFolder && (
                                    <button
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                      onClick={() => downloadDriveFile(item.id)}
                                      title="View/Download"
                                      aria-label="View/Download"
                                      disabled={isLoading}
                                    >
                                      {isLoading ? (
                                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                      ) : (
                                        <FaEye className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                  {canEdit && (
                                    <>
                                      <button
                                        className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                                        onClick={() => isFolder ? renameDriveFolder(item.id) : renameDriveFile(item.id)}
                                        title={t("projectDetails.rename")}
                                        aria-label={t("projectDetails.rename")}
                                      >
                                        <FaEdit className="w-4 h-4" />
                                      </button>
                                      <button
                                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                        onClick={() => isFolder ? deleteDriveFolder(item.id) : deleteDriveFile(item.id)}
                                        title="Delete"
                                        aria-label="Delete"
                                      >
                                        <FaTrash className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-600 text-center py-4">
                          {currentFolderId ? t("projectDetails.thisFolderIsEmpty") : t("projectDetails.noFilesInDrive")}
                        </p>
                      )}
                    </div>
                  )}
                </Card>
              </>
            )}

            {/* Project Tasks Section - Same view as Task Picker "All tasks" tab */}
            {!isNew && !isEditPath && project && (
              <Card id="tasks-section">
                <h2 className="text-xl font-bold mb-4">{t("projectDetails.tasks")}</h2>
                <AllTasksTreeView
                  bundle={projectTaskBundle}
                  loading={projectTaskBundleLoading}
                />
              </Card>
            )}

            {/* Client access (external share link) */}
            {canEdit && !isNew && !isEditPath && id && (
              <Card id="client-share-section">
                <ProjectClientSharePanel projectId={id} enabled={!!canEdit} />
              </Card>
            )}

            {/* Time Reports */}
            <Card id="time-reports-section" ref={timeReportsContainerRef}>
              <div className="flex items-center justify-between mb-4 gap-2">
                <h2 className="text-xl font-bold shrink-0">{t("projectDetails.timeReports")}</h2>
                <div className="flex items-center gap-2 min-w-0">
                <TimeReportMarkerFilter markers={timeReportMarkers} onChange={changeTimeReportMarkers} />
                {user?.isAdmin && (
                  <>
                    {/* Desktop: buttons visible */}
                    <div className="hidden md:flex items-center space-x-2">
                      {timeReportMarkers.length === 0 && (
                      <button
                        type="button"
                        className="flex items-center justify-start rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                        onClick={() => {
                          if (showAllTimeReports) {
                            setShowAllTimeReports(false);
                            fetchTimeReports(true, false);
                          } else {
                            setShowAllTimeReports(true);
                            fetchTimeReports(true, true);
                          }
                        }}
                        disabled={timeReportsLoading}
                      >
                        {showAllTimeReports ? t("projectDetails.showFirst5Only") : t("projectDetails.showAllTimeReports")}
                      </button>
                      )}
                      <button
                        type="button"
                        className="flex items-center justify-start rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                        onClick={openProjectExportModal}
                      >
                        <FaDownload className="mr-2 text-gray-700" />
                        {t("timeReports.exportToCsv")}
                      </button>
                      {exportError && (
                        <span className="text-sm text-red-500">
                          {exportError}
                        </span>
                      )}
                    </div>
                    {/* Mobile: three-dot menu */}
                    <div className="relative time-reports-menu-container md:hidden">
                      <button
                        type="button"
                        onClick={() => setTimeReportsMenuOpen((open) => !open)}
                        className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                        aria-expanded={timeReportsMenuOpen}
                        aria-haspopup="true"
                        aria-label={t("projectDetails.timeReportsActions")}
                      >
                        <FaEllipsisV className="w-5 h-5" />
                      </button>
                      {timeReportsMenuOpen && (
                        <div className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10 py-1">
                          {timeReportMarkers.length === 0 && (
                          <button
                            type="button"
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => {
                              if (showAllTimeReports) {
                                setShowAllTimeReports(false);
                                fetchTimeReports(true, false);
                              } else {
                                setShowAllTimeReports(true);
                                fetchTimeReports(true, true);
                              }
                              setTimeReportsMenuOpen(false);
                            }}
                            disabled={timeReportsLoading}
                          >
                            {showAllTimeReports ? t("projectDetails.showFirst5Only") : t("projectDetails.showAllTimeReports")}
                          </button>
                          )}
                          <button
                            type="button"
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => {
                              openProjectExportModal();
                              setTimeReportsMenuOpen(false);
                            }}
                          >
                            <FaDownload className="w-4 h-4 text-gray-500" />
                            {t("timeReports.exportToCsv")}
                          </button>
                          {exportError && (
                            <p className="px-4 py-2 text-sm text-red-500 border-t border-gray-100">
                              {exportError}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
                </div>
              </div>
              {timeReportsLoading && timeReports.length === 0 && (
                <div className="text-center py-8 text-gray-500">Loading time reports…</div>
              )}
              {timeReports && timeReports.length > 0 ? (
                <div className="space-y-3">
                  {timeReports.map((report, reportIndex) => {
                    const preferredLang = user?.languages?.ui || "English";
                    const getDisplayDescription = () =>
                      getDisplayTextFromTranslations(
                        report.translatedDescriptions,
                        report.descriptionLanguage,
                        report.description,
                        preferredLang,
                        report.descriptionDisplay
                      );

                    // Helper: open photo gallery for this report
                    const openTimeReportPhotos = () => {
                      if (!report.photos || report.photos.length === 0)
                        return;
                      const photoItems = report.photos.map((url) => ({
                        url,
                        date: report.date,
                        description: getDisplayDescription(),
                        reportId: report._id,
                      }));
                      setPhotoModal({
                        isOpen: true,
                        items: photoItems,
                        index: 0,
                      });
                    };

                    const photoCount = report.photos?.length || 0;
                    const photosToShow = report.photos?.slice(0, 1) || [];
                    const canEditReport =
                      !report.absence?.isAbsent &&
                      (user?.isAdmin || (report.worker?._id && String(report.worker._id) === String(user?.userId)));
                    const isEditing = editingReportId === report._id;
                    const mainDescription = getDisplayDescription();
                    // Description in the user's language to show below the textarea when it differs from the source.
                    const userLanguageDescriptionRaw = (
                      getDisplayTextFromTranslations(
                        report.translatedDescriptions,
                        report.descriptionLanguage,
                        report.description,
                        preferredLang,
                        report.descriptionDisplay
                      ) || ""
                    ).trim();
                    const userLanguageDescription = stripLanguageTag(userLanguageDescriptionRaw);
                    const showUserLanguageDescription =
                      userLanguageDescription &&
                      userLanguageDescription !== (report.description || "").trim();
                    const sourceLanguageFlag = getLanguageFlag(report.descriptionLanguage);
                    const userLanguageFlag = getLanguageFlag(preferredLang);

                    return (
                      <div
                        key={report._id}
                        data-report-index={reportIndex}
                        className={`relative bg-white border rounded-lg p-4 hover:shadow-md transition-all duration-200 ${
                          report.hiddenFromClient
                            ? "border-amber-300 bg-amber-50/40"
                            : "border-gray-200"
                        }`}
                      >
                        {(canEditReport || (user?.isAdmin && !report.absence?.isAbsent)) &&
                          !isEditing && (
                          <div className="absolute -top-4 -right-4 z-10 flex items-center gap-1">
                            {user?.isAdmin && !report.absence?.isAbsent && (
                              <button
                                type="button"
                                onClick={() => toggleReportClientVisibility(report)}
                                disabled={clientVisibilitySavingId === report._id}
                                className={`p-2 rounded transition-colors disabled:opacity-50 ${
                                  report.hiddenFromClient
                                    ? "text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                                    : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                }`}
                                title={
                                  report.hiddenFromClient
                                    ? t("projectDetails.showToCustomer")
                                    : t("projectDetails.hideFromCustomer")
                                }
                                aria-label={
                                  report.hiddenFromClient
                                    ? t("projectDetails.showToCustomer")
                                    : t("projectDetails.hideFromCustomer")
                                }
                              >
                                {report.hiddenFromClient ? (
                                  <FaEyeSlash className="w-4 h-4" />
                                ) : (
                                  <FaEye className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            {canEditReport && (
                              <button
                                type="button"
                                onClick={() => startEditingReport(report)}
                                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title={t("projectDetails.editReport")}
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                        {report.hiddenFromClient && !report.absence?.isAbsent && (
                          <div className="mb-2">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
                              <FaEyeSlash className="w-3 h-3" />
                              {t("projectDetails.hiddenFromCustomer")}
                            </span>
                          </div>
                        )}
                        {isEditing ? (
                          <div className="space-y-4">
                            {editError && (
                              <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                                {editError}
                              </div>
                            )}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t("projectDetails.project")}</label>
                              <select
                                value={editForm.projectId}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, projectId: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">{t("projectDetails.selectProject")}</option>
                                {editProjectsList.map((p) => (
                                  <option key={p._id} value={p._id}>
                                    {p.name}
                                    {p.address ? ` - ${p.address}` : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                              <input
                                type="date"
                                value={editForm.date}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, date: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            {editTaskBundle ? (
                              <>
                                <TaskTimeEntryList
                                  entries={editForm.taskEntries}
                                  onUpdateHours={handleEditTaskHours}
                                  onUpdateDescription={handleEditTaskDescription}
                                  onRemove={handleRemoveTaskEntryInline}
                                  onAddClick={() => setIsInlineTaskPickerOpen(true)}
                                  hideAddTask={false}
                                  allowRemove={true}
                                />
                                <div className="relative">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("projectDetails.description")}</label>
                                  <span
                                    className="absolute top-8 right-3 text-lg leading-none pointer-events-none"
                                    title={report.descriptionLanguage || ""}
                                    aria-hidden
                                  >
                                    {sourceLanguageFlag}
                                  </span>
                                  <textarea
                                    value={editForm.description}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({ ...prev, description: e.target.value }))
                                    }
                                    rows={2}
                                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                    placeholder={t("projectDetails.optionalGeneralDescription")}
                                  />
                                  {showUserLanguageDescription && (
                                    <p className="mt-1 text-sm text-gray-500 flex items-start gap-1.5">
                                      <span className="flex-shrink-0 text-base" aria-hidden>{userLanguageFlag}</span>
                                      <span>{userLanguageDescription}</span>
                                    </p>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={editForm.hours}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({ ...prev, hours: e.target.value }))
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="relative">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("report.descriptionRequired")}</label>
                                  <span
                                    className="absolute top-8 right-3 text-lg leading-none pointer-events-none"
                                    title={report.descriptionLanguage || ""}
                                    aria-hidden
                                  >
                                    {sourceLanguageFlag}
                                  </span>
                                  <textarea
                                    value={editForm.description}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({ ...prev, description: e.target.value }))
                                    }
                                    rows={3}
                                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                  />
                                  {showUserLanguageDescription && (
                                    <p className="mt-1 text-sm text-gray-500 flex items-start gap-1.5">
                                      <span className="flex-shrink-0 text-base" aria-hidden>{userLanguageFlag}</span>
                                      <span>{userLanguageDescription}</span>
                                    </p>
                                  )}
                                </div>
                              </>
                            )}
                            <div className="flex gap-2 pt-2">
                              <button
                                type="button"
                                onClick={saveEditedReport}
                                disabled={editSaving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                              >
                                {editSaving ? t("projectDetails.saving") : t("projectDetails.save")}
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditingReport}
                                disabled={editSaving}
                                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
                              >
                                {t("projectDetails.cancel")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-baseline mb-2">
                                  <div className="text-lg font-semibold text-gray-900">
                                    {new Date(report.date).toLocaleDateString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      }
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="text-base font-bold text-gray-700 bg-gray-100 px-3 py-1 rounded">
                                      {report.hours}{t("timeReports.hourSuffix")}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-sm text-gray-600 font-medium">
                                  {report.worker?.name || t("projectDetails.unknownWorker")}
                                </div>
                                {report.flags?.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {report.flags.map((flag) => (
                                      <span
                                        key={flag._id}
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                                          flag.status === "resolved"
                                            ? "bg-green-50 text-green-700 border-green-200"
                                            : "bg-amber-50 text-amber-700 border-amber-200"
                                        }`}
                                      >
                                        {t(`reportFlags.${flag.type === "missing_task" ? "missingTask" : flag.type}`)}
                                        {" · "}
                                        {t(`reportFlags.status${flag.status === "resolved" ? "Resolved" : "Open"}`)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {photoCount > 0 && (
                                <div
                                  className="flex-shrink-0 cursor-pointer group"
                                  onClick={openTimeReportPhotos}
                                >
                                  <div className="flex -space-x-2 mb-2">
                                    {photosToShow.map((photoUrl, idx) => (
                                      <img
                                        key={idx}
                                        src={getThumbUrl(photoUrl)}
                                        alt={`Photo ${idx + 1}`}
                                        className="w-16 h-16 rounded-lg border-2 border-white object-cover shadow-md group-hover:scale-105 transition-transform"
                                      />
                                    ))}
                                  </div>
                                  <div className="text-xs text-gray-600 text-center font-medium">
                                    {photoCount}{" "}
                                    {photoCount === 1 ? t("projectDetails.photo") : t("projectDetails.photos")}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-100 w-full">
                              {mainDescription && (
                                <div className="text-gray-800 leading-relaxed mb-2">
                                  {mainDescription}
                                </div>
                              )}
                              {report.internalNote?.trim() && (
                                <div className="mb-2 p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                                  <p className="text-xs font-medium text-slate-600 mb-0.5">
                                    {t("report.internalNote")}
                                  </p>
                                  <p className="text-sm text-slate-800 whitespace-pre-wrap">
                                    {report.internalNote}
                                  </p>
                                </div>
                              )}
                              {report.taskEntries && report.taskEntries.length > 0 && (
                                <div className="space-y-2">
                                  {report.taskEntries.map((entry, idx) => {
                                    const isSubtask = !!(entry.subtaskId != null && entry.subtaskId !== "");
                                    const EntryIcon = isSubtask ? FaListUl : FaTasks;
                                    const parentTaskLabel = isSubtask ? parentTaskLabelForTimeEntry(entry) : null;
                                    return (
                                    <div key={idx} className="text-sm">
                                      <div className="flex items-center justify-between mb-1 gap-2">
                                        <span className="font-medium text-gray-900 flex items-start gap-2 min-w-0 flex-1">
                                          <EntryIcon className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" aria-hidden />
                                          <span className="min-w-0 flex flex-col items-stretch">
                                            {parentTaskLabel && (
                                              <span className="text-xs font-normal text-gray-500 truncate" title={parentTaskLabel}>
                                                {parentTaskLabel}
                                              </span>
                                            )}
                                            <span className="truncate">{displayText(entry, "title") || entry.title || `Task ${idx + 1}`}</span>
                                          </span>
                                        </span>
                                        <span className="text-gray-600 font-semibold flex-shrink-0">
                                          {entry.hours}{t("timeReports.hourSuffix")}
                                        </span>
                                      </div>
                                      {(displayText(entry, "description") || entry.description) && (
                                        <p className="text-gray-600 text-xs mt-0.5 pl-2 border-l-2 border-gray-200">
                                          {displayText(entry, "description") || entry.description}
                                        </p>
                                      )}
                                    </div>
                                  );})}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : !timeReportsLoading ? (
                <p className="text-sm text-gray-600 text-center py-8">
                  {timeReportMarkers.length
                    ? t("projectDetails.noTimeReportsMatchFilter")
                    : t("projectDetails.noTimeReportsYet")}
                </p>
              ) : null}

              {timeReportsHasMore && timeReportMarkers.length === 0 && (
                <div className="mt-4 text-center">
                  <button
                    className="px-6 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-gray-700"
                    onClick={() => {
                      setPreviousTimeReportsCount(timeReports.length);
                      fetchTimeReports(false);
                    }}
                    disabled={timeReportsLoading}
                  >
                    {timeReportsLoading ? t("projectDetails.loading") : showAllTimeReports ? t("projectDetails.load100More") : t("projectDetails.load5More")}
                  </button>
                </div>
              )}
            </Card>

            {/* Photos */}
            <Card ref={photosContainerRef} id="photos-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">{t("projectDetails.photosSection")}</h2>
                {!photosLoading && photos && photos.length > 0 && (
                  <button
                    type="button"
                    className="flex items-center justify-start rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                    onClick={downloadAllPhotos}
                  >
                    <FaDownload className="mr-2 text-gray-700" />
                    {t("projectDetails.downloadAll")}
                  </button>
                )}
              </div>
              {photosLoading && <div className="text-center py-8 text-gray-500">{t("projectDetails.loadingPhotos")}</div>}
              {!photosLoading && photos && photos.length > 0 ? (
                <>
                  <div className="text-sm text-gray-600 mb-4">
                    {photos.length}{" "}
                    {photos.length === 1 ? t("projectDetails.photo") : t("projectDetails.photos")}
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                    {photos.map((p, idx) => {
                      const displayDesc = getDisplayDescription(p);
                      return (
                        <button
                          key={`${p.reportId}-${idx}`}
                          data-photo-index={idx}
                          className="relative group rounded-lg overflow-hidden aspect-square"
                          onClick={() => openPhotoModalAt(idx)}
                          title={
                            displayDesc
                              ? displayDesc.length > 100
                                ? displayDesc.substring(0, 100) + "…"
                                : displayDesc
                              : ""
                          }
                        >
                          <img
                            src={getThumbUrl(p.url)}
                            alt={displayDesc || `Photo ${idx + 1}`}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black bg-opacity-40 flex items-end p-2 text-white text-xs transition-opacity duration-200">
                            <div className="truncate w-full text-left">
                              {displayDesc || ""}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {photosHasMore && (
                    <div className="mt-4 text-center">
                      <button
                        className="px-6 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-gray-700"
                        onClick={loadMorePhotos}
                        disabled={photosLoading}
                      >
                        {photosLoading ? "Loading…" : "Load more"}
                      </button>
                    </div>
                  )}
                </>
              ) : !photosLoading ? (
                <p className="text-sm text-gray-600 text-center py-8">{t("projectDetails.noPhotos")}</p>
              ) : null}
            </Card>
          </div>
        )}

        {previewFile && (
          <PreviewModal
            file={previewFile}
            onClose={() => setPreviewFile(null)}
            t={t}
          />
        )}

        <EconomyHoursDetailModal state={economyHoursModal} onClose={closeEconomyHoursModal} t={t} />
        <EconomyMaterialsDetailModal
          state={economyMaterialsModal}
          onClose={closeEconomyMaterialsModal}
          t={t}
        />
        <EconomyExpectedCostsDetailModal
          state={economyExpectedCostsModal}
          onClose={closeEconomyExpectedCostsModal}
          onSettle={handleExpectedCostSettle}
          onEdit={handleExpectedCostEdit}
          onDelete={handleExpectedCostDelete}
          actionId={expectedCostActionId}
          t={t}
        />
        <ExpectedMaterialCostFormModal
          isOpen={expectedCostFormModalOpen}
          projectId={id}
          taskBundle={projectTaskBundle}
          editingAccrual={editingExpectedCost}
          onClose={() => {
            setExpectedCostFormModalOpen(false);
            setEditingExpectedCost(null);
          }}
          onSaved={() => {
            refetchEconomyAfterAccrualChange();
            if (economyExpectedCostsModal.open && economyExpectedCostsModal.taskContext) {
              void openEconomyTaskExpectedCostsModal(economyExpectedCostsModal.taskContext);
            }
          }}
        />
        <ExportProjectCsvModal
          isOpen={projectExportModalOpen}
          startDate={projectExportStartDate}
          onStartDateChange={setProjectExportStartDate}
          onClose={closeProjectExportModal}
          onConfirm={() => handleExportProjectCSV(projectExportStartDate)}
          isSubmitting={projectExportSubmitting}
          filterSummary={timeReportMarkerLabels(timeReportMarkers, t).join(", ")}
          t={t}
        />
        <ExportProjectCsvModal
          isOpen={economyExportModalOpen}
          startDate={economyExportStartDate}
          onStartDateChange={setEconomyExportStartDate}
          onClose={closeEconomyExportModal}
          onConfirm={() => handleExportEconomyCSV(economyExportStartDate)}
          isSubmitting={economyExportSubmitting}
          t={t}
        />

        {/* Download progress modal */}
        {photosDownloading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded shadow-lg w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">{t("projectDetails.downloadingPhotos")}</h3>
                <button
                  className="text-sm text-red-600"
                  onClick={() => {
                    // abort
                    if (photosDownloadControllerRef.current) {
                      photosDownloadControllerRef.current.abort();
                    }
                    setPhotosDownloading(false);
                  }}
                >
                  {t("projectDetails.cancel")}
                </button>
              </div>

              <div className="mb-2">
                {photosDownloadTotalParts > 0 ? (
                  <div className="text-sm text-gray-700">
                    {t("projectDetails.partOfTotal", { current: photosDownloadPart, total: photosDownloadTotalParts })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">
                    {t("projectDetails.preparingDownload")}
                  </div>
                )}
              </div>

              <div className="w-full bg-gray-200 rounded h-3 overflow-hidden">
                <div
                  className="bg-brand h-3"
                  style={{ width: `${photosDownloadProgress.percent || 0}%` }}
                />
              </div>

              <div className="mt-3 text-sm text-gray-600">
                {photosDownloadProgress.percent
                  ? `${photosDownloadProgress.percent}%`
                  : "Working…"}
              </div>
            </div>
          </div>
        )}

        {photoModal.isOpen && (
          <PhotoCarouselModal
            isOpen={photoModal.isOpen}
            onClose={closePhotoModal}
            items={photoModal.items}
            initialIndex={photoModal.index}
          />
        )}

        <TaskPickerModal
          isOpen={isInlineTaskPickerOpen}
          onClose={() => setIsInlineTaskPickerOpen(false)}
          onSelect={handleAddTaskEntryInline}
          bundle={editTaskBundle}
          recentTasks={editRecentTasks}
          onBundleUpdated={editTaskBundle ? (newBundle) => setEditTaskBundle(newBundle) : undefined}
        />
      </PageWrapper>
    </>
  );
};

export default ProjectDetailsView;
